import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { DatabaseWrapper, DatabaseWrapperProvider } from 'services/database-wrapper';
import * as Sentry from '@sentry/angular';
import { CloudFunctionAuthError, CloudFunctionsService } from 'services/cloud-functions.service';
import { errorReportFields, isTransientFetchError } from 'services/error-patterns';
import { evaluateCouchLoginStuck, STUCK_DEFERRED_LOGIN_MS, StuckEvalTrigger } from 'services/couch-login-stuck';
import { AuthSettleMeasurement, awaitFirebaseUserPresentAsync, measureAuthStateSettleAsync, realAuthSettleEnvironment } from 'services/couch-login-firebase-ready';
import { resolveCouchLoginOnlineGate } from 'services/couch-login-online-gate';
import { resolveRetryCouchLoginGate } from 'services/couch-login-retry-gate';
import { CouchLoginSkipReason, isOsRequestTimeoutCouchLoginError, isRecoverableCouchLoginError, isVersionMismatchCouchLoginError } from 'services/couch-login-error-classify';
import { awaitUnlessAbortedAsync, isUsersDocConflictResponseAsync, usersDocConflictRetryWaitMs } from 'services/couch-fetch-conflict-retry';
import {
  awaitBoundedLive, fetchSessionEndpointAsync, BoundedWaitResult,
  COUCH_LOGIN_WAIT_TIMEOUT_MS, COOKIE_CLEAR_TIMEOUT_MS,
  sessionRequestOutcomeOf, sessionRequestOutcomeFields, wasHeldNetworkFailure,
} from 'services/couch-login-wait-bound';
import { FailedNotingFreeze, settleNotingFreezeAsync, SettledOutcome } from 'services/running-clock';
import { FocusState, FocusStateService } from 'services/focus-state.service';
import { ToastController } from 'providers/toast-controller';
import { PageGuardRefusalError } from 'providers/page-lifecycle-guard';
import { AuthRole, authRoleFromStrings, CouchLoginResult } from '@cf-shared/couch-auth';
import { roleAfterLicenceExpiry } from '@cf-shared/effective-role';
import { SettingsService } from 'services/settings.service';
import { DeferredPromise, isTokenExpired, sleep } from '@cf-shared/shared-util';
import { DateTime } from 'luxon';
import { FirebaseService } from './firebase.service';
import { PopupResolverWarmupOutcome, PopupResolverWarmupState } from './popup-resolver-warmup';
import { AuthProvider, browserPopupRedirectResolver, createUserWithEmailAndPassword, FacebookAuthProvider, fetchSignInMethodsForEmail, getAdditionalUserInfo, GoogleAuthProvider, OAuthProvider, onAuthStateChanged, sendPasswordResetEmail, signInWithCredential, signInWithCustomToken, signInWithEmailAndPassword, signInWithPopup, signOut, updateProfile, User, UserCredential } from 'firebase/auth';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { CapacitorCookies } from '@capacitor/core';
import { UseActivationKeyResults } from '@cf-shared/activation-codes-interface';
import { LoggingService } from './logging.service';
import { SessionHealth } from './session-health';
import { CouchUserInfo, SubscriptionStatus, UserLicenseType } from '@cf-shared/data-model/couch-user';
import { effectiveCoachSlots, isActiveCoach, hasActiveCoachSeat } from '@cf-shared/data-model/coach';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { distinctUntilChanged } from 'rxjs/operators';
import { isAppStandalone, isCapacitorNative } from 'environments/environment-helpers';
import { getRootDomain } from 'environments/environment-helpers';
import { getMyApp } from 'app/app-interface';
import { bootTimings, BootPhase } from 'app/boot-timings';
import { LL } from 'i18n/i18n.service';
import { AppEnvironment } from 'environments/environment';
import { TelemetryService } from './telemetry.service';
import { UrlQueryService } from './url-query-service';
import { ConnectionState, OnlineStateService } from './online-state.service';
import * as WarmResumeStorage from './warm-resume-storage';
import { warmResumeMustWaitForCatalog, WarmResumeUserAuth } from './warm-resume-storage';
import { localSharedDbBaseName } from './local-user-db-name';
import { clearUserScopedLocalStorage } from './local-storage-hygiene';
import { SQLITE_READY_BOOTS_KEY } from './local-database-engine-decisions';
import { setCouchSessionHint } from './couch-session-hint';
import { assertUsersDocVersionSupportedOnClient } from 'util/users-doc-version-guard';
import PouchDB from 'pouchdb-browser';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { CouchLoginReason, STORM_REPORT_COOLDOWN_MS, resolveCouchLoginStormReport } from './couch-login-storm';
import { markCouchLoginStormReported, recordCouchLoginStart } from './couch-login-storm-store';
import { TranslatedString } from 'models/translated-string';
import { NavController } from 'providers/nav-controller';
import { PostLoginRouteClaim } from 'providers/post-login-route-coordination';
import { describeSocialLoginResultForLog } from './social-login-log';
import { FatalErrorKind } from 'pages/fatal-error/fatal-error-interface';

export interface UserAuth {
  name: string;
  email: string;
  avatarUrl: string;
  uid: string;
  method: string;
}

// compile-time structural equivalence check for UserAuth ≡ WarmResumeUserAuth;
// never executed — fails to compile if the two shapes drift. `WarmResumeUserAuth`
// is duplicated in warm-resume-storage.ts to keep that module import-free; this
// assertion guards against silent drift if either interface gains a field.
// Exported only to satisfy TS's `noUnusedLocals`; do not call.
export function _checkWarmResumeUserAuthEquivalence(u: UserAuth, w: WarmResumeUserAuth) {
  const toWarmResume: WarmResumeUserAuth = u;
  const toUserAuth: UserAuth = w;
  return [toWarmResume, toUserAuth];
}

export function asciiToHex(str) {
  let arr1 = [];
  for (let n = 0, l = str.length; n < l; n++) {
    let hex = Number(str.charCodeAt(n)).toString(16);
    arr1.push(hex);
  }
  return arr1.join('');
}

function extractLoginMethodFromFirebaseUser(user: User) {
  return user.providerData.map(provider => provider.providerId).join(', ');
}

function getWebViewType(): string | undefined {
  // Capacitor native is not a social-media webview — allow full auth
  if (isCapacitorNative()) return undefined;

  const userAgent = navigator.userAgent || navigator.vendor;

  // General patterns that might indicate a web view
  const webViewPatterns = [
    { name: 'Facebook', pattern: /FBAN|FBAV/i },
    { name: 'Instagram', pattern: /Instagram/i },
    { name: 'Line', pattern: /Line/i },
    { name: 'WhatsApp', pattern: /WhatsApp/i },
    { name: 'Snapchat', pattern: /Snapchat/i },
    { name: 'Twitter', pattern: /Twitter/i },
    { name: 'iOS WebView', pattern: /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i },
    { name: 'Android WebView', pattern: /Android.*Version\/[\d.]+.*Chrome\/[\d.]+/i },
  ];

  for (const webView of webViewPatterns) {
    if (webView.pattern.test(userAgent)) {
      return webView.name;
    }
  }

  // Common token in Android web views
  if (/\b(wv)\b/.test(userAgent)) {
    return 'Android WebView';
  }

  // Android web view pattern
  if (/Android.*Version\/[\d.]+.*Chrome\/[\d.]+/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    return 'Android WebView';
  }

  return undefined;
}

/**
 * #3904 — upper bound on how long cold-start boot waits for Firebase auth-init
 * to settle (`authStateReady()`). The Firebase SDK can get stuck re-issuing
 * accounts:lookup during initializeCurrentUser() (the Android boot-hang cohort),
 * so an unbounded await wedges boot forever. 8s is longer than the 5s couch-login
 * bound (this one gates first paint) yet short enough to avoid a long perceived
 * hang; a legitimately slow-but-fine restore on a poor connection still settles
 * well within it. On timeout, boot proceeds and the onAuthStateChanged listener
 * routes the user once (if) Firebase eventually settles.
 */
export const BOOT_AUTH_SETTLE_TIMEOUT_MS = 8000;

@Injectable()
export class AuthService {
  private dbProvider: DatabaseWrapperProvider = undefined; // used for forwardRef()

  // warm-resume storage helpers — implementation lives in warm-resume-storage.ts
  // so it can be unit-tested without pulling in AuthService's Angular/Sentry deps.
  // The pure module uses a local `WarmResumeUserAuth` interface (structurally
  // identical to `UserAuth` but without the `auth.service.ts` import), so the
  // signatures get cast to the canonical `UserAuth` type here. The compile-time
  // checks below ensure the two shapes stay equivalent — if `UserAuth` gains a
  // required field, one of the assertions will fail to compile.
  static getWarmResumeUid = WarmResumeStorage.getWarmResumeUid;
  static getWarmResumeUserAuth = WarmResumeStorage.getWarmResumeUserAuth as () => UserAuth | null;
  static setWarmResumeFlag = WarmResumeStorage.setWarmResumeFlag as (user: UserAuth) => void;
  static clearWarmResumeFlag = WarmResumeStorage.clearWarmResumeFlag;

  /* Replay last value, but don't return anything until initialized.
  (Otherwise, we get a "signed-out" flashing for a second on each boot! )*/
  private mainAuthSubject = new ReplaySubject<UserAuth>(1);
  public readonly userAuth = this.mainAuthSubject.asObservable();
  public user: UserAuth;
  private ignoreLoginStateChange = false;
  private suppressNullAuth = false; // when OTP signInToken is in URL, suppress initial null emission

  private webviewType: string | undefined = undefined;

  public couch: CouchLoginResult;

  // emits whenever `this.couch` changes — most importantly when the CouchDB
  // login completes AFTER the user was already emitted (the warm-resume path
  // emits the user with couch=null and logs into CouchDB later in Phase B).
  // the consent dialog re-evaluates on this signal: the account's consent
  // records only arrive with the CouchDB login, so evaluating on the Firebase
  // login event alone always ran with no records on a warm resume (#4219 —
  // the account→device consent copy never applied on native, and a
  // record-less account was never retro-asked). BehaviorSubject so a late
  // subscriber immediately sees the current state.
  private couchAuthSubject = new BehaviorSubject<CouchLoginResult | null>(null);
  public readonly couchAuthChanged = this.couchAuthSubject.asObservable();

  // this triggers after user has logged in, databases have loaded etc
  public whenAppReady = DeferredPromise.defer<boolean>();

  // resolves once Firebase has settled its initial auth state — either via
  // authStateReady() on the cold-start path, or via the first
  // onAuthStateChanged callback on the warm-resume fast path (#3184). after
  // this resolves, fbAuth.currentUser is the source of truth (populated for
  // logged-in users, null for logged-out). token-dependent callers (e.g.
  // runElasticQueryAsync in food-search-elastic.ts) await this before reading currentUser, so
  // the warm-resume fast path doesn't fire ES requests without an
  // Authorization header (#3246).
  public whenFirebaseAuthSettled = DeferredPromise.defer<void>();
  private firebaseAuthSettled = false;
  private resolveFirebaseAuthSettled() {
    if (this.firebaseAuthSettled) return;
    this.firebaseAuthSettled = true;
    this.whenFirebaseAuthSettled.resolve();
  }

  // database used by admin users to configure other users
  public allUsersDb: DatabaseWrapper = null; // initialized on first use

  private wasEverLoggedIn = false;
  private lastCouchLoginError: any;
  // #6231: why the most recent attempt ended without calling the server (null when it did call
  // it, or is still running). Reset at the start of every attempt, like lastCouchLoginError.
  private lastCouchLoginSkip: { reason: CouchLoginSkipReason; firebaseSettled?: boolean } | null = null;
  signingIn: boolean = false; // used to prevent blinking of login page while signing in

  // public view for OnlineStateService (lastCouchLoginError itself stays private):
  // whether the most recent couch-login failure was an expected, recoverable condition
  // (see isRecoverableCouchLoginError in couch-login-error-classify.ts). The field is
  // reset at the start of every attempt, so this always describes the CURRENT (most
  // recent) attempt, never a stale earlier flap.
  public isLastCouchLoginErrorRecoverable(): boolean {
    return this.lastCouchLoginError != null && isRecoverableCouchLoginError(this.lastCouchLoginError);
  }

  // #4283: was the most recent couch-login failure specifically the "this server is
  // running an older format version than your account" refusal (HTTP 409 from
  // couchLogin, thrown server-side as UsersDocTooNewError)? Split out from the broader
  // isLastCouchLoginErrorRecoverable() so callers that want to log or message this
  // specific, distinct condition (rather than lump it in with a generic network blip)
  // can do so.
  public isLastCouchLoginErrorVersionMismatch(): boolean {
    return this.lastCouchLoginError != null && isVersionMismatchCouchLoginError(this.lastCouchLoginError);
  }

  // df-97e69e66c032: was the most recent couch-login failure the iOS system timeout ("The request
  // timed out.")? Not recoverable in the shared sense (a slow server stays reported), but it is
  // not an authentication failure either, so OnlineStateService handles it on its own.
  public isLastCouchLoginErrorOsTimeout(): boolean {
    return this.lastCouchLoginError != null && isOsRequestTimeoutCouchLoginError(this.lastCouchLoginError);
  }

  // #4617: roughly how old this Firebase account is, in whole hours, or undefined when Firebase
  // has not settled yet. Telemetry only, and deliberately coarse: it exists so one boot-time
  // report can tell "somebody who signed up minutes ago" apart from "somebody whose account is
  // months old", which is the difference between a genuinely new user and an existing one who
  // cannot reach their data. An age in hours cannot identify anyone, unlike the creation
  // timestamp it is derived from, so that timestamp stays inside this method.
  public getAccountAgeHours(): number | undefined {
    const createdAt = this.firebase.fbAuth.currentUser?.metadata?.creationTime;
    if (!createdAt) return undefined;

    // Firebase hands this back as an RFC-1123 date string ("Tue, 02 Sep 2026 06:45:11 GMT")
    const created = DateTime.fromHTTP(createdAt, { zone: 'utc' });
    if (!created.isValid) return undefined;

    return Math.floor(DateTime.utc().diff(created, 'hours').hours);
  }

  // #6231: why the most recent couch-login attempt ended without calling the server, or null
  public getLastCouchLoginSkipReason(): CouchLoginSkipReason | null {
    return this.lastCouchLoginSkip?.reason ?? null;
  }

  // name/message of the most recent couch-login failure, for correctly-labeled
  // telemetry context at the report site (keeps the field itself private). #6231: an attempt
  // that never called the server stores no error, so the report names its skip reason instead.
  public getLastCouchLoginErrorReport(): { name?: string; message?: string; skip_reason?: string; firebase_settled?: boolean } {
    return {
      name: this.lastCouchLoginError?.name,
      message: this.lastCouchLoginError?.message,
      skip_reason: this.lastCouchLoginSkip?.reason,
      firebase_settled: this.lastCouchLoginSkip?.firebaseSettled,
    };
  }

  // #3493: when couchLogin fails with a transient network / abort error, we set
  // `this.couch = null` and proceed in "offline mode" — but the splash never
  // resolves cleanly downstream. This flag lets the splash UI know to show a
  // Retry affordance, and the focus / online listeners installed below
  // auto-retry once conditions improve.
  public couchLoginRetryNeeded = false;
  private couchLoginRetryListenersInstalled = false;
  private couchLoginRetryInFlight = false;
  // df-53c89a39b0e6: the claim on the post-login move a succeeded login retry hands to the
  // post-login boot its re-emit starts. See takeRetryRouteClaim
  private retryRouteClaimForNextBoot: PostLoginRouteClaim | undefined;

  // Sentry triage v1.25.0 Group 3: a deferred couch login (offline / flaky network)
  // is the expected, recoverable path, so we no longer report it as an error. Instead
  // a one-shot "stuck" watchdog reports CouchLoginStuckOffline only if the login stays
  // deferred while the user keeps waiting. State for that episode lives here; the
  // (node-tested) decision is in services/couch-login-stuck.ts.
  private couchLoginDeferredAt: DateTime | null = null;
  private couchLoginStuckReported = false;
  private couchLoginStuckTimer: ReturnType<typeof setTimeout> | null = null;

  // the consent dialog gates the app until the user has accepted the ToS
  // this is just held in this service, but the dialog is in the consent-dialog component
  public licenseDialogSubject = new BehaviorSubject<boolean>(false);
  public readonly licenseDialog = this.licenseDialogSubject.asObservable().pipe(distinctUntilChanged()); // emit only on transitions

  constructor(
    private cloudFunctionsService: CloudFunctionsService,
    private settings: SettingsService,
    private telemetry: TelemetryService,
    private firebase: FirebaseService,
    public logging: LoggingService,
    private onlineStateService: OnlineStateService,
    private urlQuery: UrlQueryService,
    private focusState: FocusStateService,
    private toastCtrl: ToastController,
    private navCtrl: NavController,
  ) { }
  registerDbProvider(dbProvider: DatabaseWrapperProvider) {
    this.dbProvider = dbProvider;
  }

  async initAsync() {
    await this.firebase.initialized;

    // Register with OnlineStateService to avoid circular dependency
    this.onlineStateService.setAuthService(this);

    // check and remember whether the user is in a web view
    this.webviewType = getWebViewType();// || 'test webview';
    if (this.webviewType) {
      this.telemetry.trackEvent('webview', { type: this.webviewType, userAgent: navigator.userAgent });
    }

    // if OTP login is pending, suppress the initial null auth emission
    // so the app stays in loading state until OTP auth completes
    if (this.urlQuery.getUrlParam('signInToken')) {
      this.suppressNullAuth = true;
    }

    // Warm-resume fast path (issue #3184): if we have a full cached UserAuth,
    // synthesize login immediately and don't await authStateReady(). On Android
    // with transient DNS failure, Firebase's internal token-refresh probe can
    // block authStateReady() for ~10 s — but the user is already logged in
    // locally and the full PouchDB is on device, so there's no reason to wait.
    // Firebase's onAuthStateChanged fires later and the idempotency guard in
    // onLoginStateChange makes the repeat callback a no-op when UIDs match.
    // decided once, here, before both readers of the flag: the cached-login branch below and the
    // warm branch of onLoginStateChange, and then app.component's Phase A choice. clearing the flag
    // makes all three take the cold start, which logs in to CouchDB before anything is read
    this.dropWarmResumeIfCatalogNotDownloaded();
    const cachedAuth = !this.suppressNullAuth ? AuthService.getWarmResumeUserAuth() : null;

    // Warm-resume synthesis MUST run synchronously BEFORE registering the Firebase
    // listener, so `this.user` is already set when Firebase eventually fires
    // onAuthStateChanged — that way the idempotency guard in onLoginStateChange
    // catches the repeat callback regardless of whether Firebase resolves in a
    // microtask or minutes later (issue #3184).
    if (cachedAuth) {
      console.log(`Auth: Warm resume fast path — synthesizing login for ${cachedAuth.email} (${cachedAuth.uid})`);
      this.applyCachedUserAuth(cachedAuth);
      // do NOT resolve whenFirebaseAuthSettled yet — fbAuth.currentUser is
      // still null until the listener below fires for the first time.
    } else {
      // cold start / no warm-resume cache: wait for auth state to be ready
      // before registering listeners so we don't get an intermediate null
      // state while Firebase checks stored tokens.
      //
      // #3904: bound this wait. The Firebase Auth SDK can get stuck re-issuing
      // accounts:lookup during initializeCurrentUser() (the Android boot-hang
      // cohort on Sentry FreezeWatchdog:BootLog), so authStateReady() never
      // resolves and a bare await wedges the whole boot forever. If it doesn't
      // settle within the bound, proceed anyway: register the listeners below and
      // let the first onAuthStateChanged fire route the user once (if) Firebase
      // eventually settles — the same "let the listener catch up" model the
      // warm-resume branch above relies on.
      //
      // #6096: the limit counts running time, so time the phone kept the app frozen
      // in the background does not use it up (it used to fire on waking and report a
      // stall that never happened). design-docs/boot-sequence.md, "#6096".
      const settled = await this.waitForAuthSettleAsync('cold-start', BOOT_AUTH_SETTLE_TIMEOUT_MS);
      if (settled) {
        // currentUser is now Firebase's truth — token-dependent callers can read it.
        // the listener below also tries to resolve on its first fire; the helper
        // guards against double-resolve so it's safe either way.
        this.resolveFirebaseAuthSettled();
      } else {
        // boot auth-init did not settle in time; waitForAuthSettleAsync has already
        // reported it. Do NOT resolve whenFirebaseAuthSettled here: currentUser is
        // still transient, so leave it to the onAuthStateChanged first-fire (which
        // calls resolveFirebaseAuthSettled) if Firebase settles.
        //
        // #6995: time the wait for that first fire. The boot cannot go on without it,
        // and on a bad network it ran 20-40 s with nothing measuring it.
        bootTimings.start(BootPhase.AuthLateSettle);
      }
    }

    onAuthStateChanged(
      this.firebase.fbAuth,
      async user => {
        // first fire on either path is the moment when fbAuth.currentUser
        // becomes Firebase's truth (cold path resolved above when it settled in
        // time; warm path — and the #3904 cold-start timeout path — resolve
        // here). the helper guards against re-resolving on subsequent auth-state
        // transitions.
        this.resolveFirebaseAuthSettled();
        // #6995: closes the late-settle bracket opened above; a no-op on every
        // path that never opened it, and on every later fire
        bootTimings.end(BootPhase.AuthLateSettle);
        await this.onLoginStateChange(user);
      },
      err => {
        // #3878: surface Firebase auth-observer errors (e.g. the accounts:lookup HTTP 400
        // token rejection seen on web) instead of only console.error — otherwise the cause
        // of a genuine NoFirebaseUser never reaches a debugger. trackSilentProblem so we can
        // size and diagnose it; the firebase_settled flag on the NoFirebaseUser event below
        // tells this (rejected-session) case apart from the readiness race.
        console.error('Auth: login error:', err);
        // narrow once: Firebase auth-observer errors are Error; a FirebaseError also carries `code`
        const authErr = err as Error & { code?: string };
        this.logging.trackSilentProblem('FirebaseAuthStateError', {
          name: authErr?.name,
          message: authErr?.message,
          code: authErr?.code,
        });
      },
    );
  }

  public getAuthRole(): AuthRole {
    return this.getAuthRoleAndExpiry().role;
  }

  /** Effective coach client-slot cap, INCLUDING an active app-store tier
   *  (`coachTierData.tierCap`) — not just the legacy/admin `coachMaxSlots`.
   *  Drives the "is a coach" gates and the coach-settings slot cap. See
   *  `effectiveCoachSlots` in @cf-shared. */
  public getEffectiveCoachSlots(): number {
    return effectiveCoachSlots(this.couch);
  }

  /** Whether the user is a coach of ANY kind — a fixed-tier / legacy coach
   *  (`effectiveCoachSlots > 0`) OR a Paddle per-seat coach (active
   *  `coachSeatData`). This is the boolean the coaching UI gates on, so a
   *  Paddle seat coach (uncapped, `effectiveCoachSlots === 0`) is still
   *  recognized. See `isActiveCoach` in @cf-shared. */
  public getIsCoach(): boolean {
    return isActiveCoach(this.couch);
  }

  /** Whether the user is specifically a Paddle per-seat (web) coach — used
   *  where a seat coach needs the uncapped / "unlimited slots" treatment
   *  (the coach-settings slot display + at-capacity check). See
   *  `hasActiveCoachSeat` in @cf-shared. */
  public getHasCoachSeat(): boolean {
    return hasActiveCoachSeat(this.couch);
  }

  public getCanCancelSubscription(): boolean {
    let role = this.getAuthRole();
    return role >= AuthRole.Paid && this.couch && this.couch.canCancelSubscription;
  }

  /** df-53c89a39b0e6: the post-login boot takes the claim a succeeded login retry handed it, and
   *  releases it once its navigation start-up is over. Undefined for every other boot */
  public takeRetryRouteClaim(): PostLoginRouteClaim | undefined {
    const claim = this.retryRouteClaimForNextBoot;
    this.retryRouteClaimForNextBoot = undefined;
    return claim;
  }

  public getCanResumeSubscription(): boolean {
    return this.couch && this.couch.subscriptionStatus === SubscriptionStatus.Suspended;
  }

  getWasEverLoggedIn() {
    return this.wasEverLoggedIn;
  }

  isWebView(): boolean {
    return !!this.webviewType;
  }

  public cacheLicenseActivation(results: UseActivationKeyResults) {
    if (results.error) {
      return;
    }

    let freshRole = results.role;
    let expiry_utc = results.licenseExpires_utc;
    if (this.settings.cachedRole !== freshRole || this.settings.cachedRoleExpiry_utc !== expiry_utc) {
      this.settings.cachedRole = freshRole;
      this.settings.cachedRoleExpiry_utc = expiry_utc;
      this.saveRememberedRoleInBackground();
    }
  }

  /**
   * Save the remembered role without waiting for it. After sign-out the database is often
   * already shut down, so a teardown refusal is expected and ignored: the role is already on
   * this device and is saved again when it next differs. See pass-through-db.md.
   */
  private saveRememberedRoleInBackground(): void {
    void this.settings.saveAsync().catch(err => {
      if (isDatabaseTornDown(err)) return;
      this.logging.trackSilentProblem('auth: saving the remembered role failed', {
        error: describeErrorForLog(err),
      });
    });
  }

  public getAuthRoleAndExpiry(): { role: AuthRole; expiry_utc: string; subscriptionExpired?: boolean } {
    let now_utc = DateTime.utc();
    let auth = this.couch;
    // only fall back to the remembered role when the server told us NOTHING. An
    // answer that carries an EMPTY roles list is a real answer — it says this
    // user holds no role — so it is believed rather than overridden by the
    // remembered one. A MISSING roles field is treated as "nothing" instead,
    // because the server always sets it, so its absence is an unexpected shape.
    // Why this distinction matters: design-docs/coach-subscription-lapse.md.
    if (auth == null || !auth.roles) {
      if (
        !this.settings.cachedRoleExpiry_utc ||
        this.settings.cachedRoleExpiry_utc === 'never' ||

        DateTime.fromISO(this.settings.cachedRoleExpiry_utc).plus({ days: 2 }) > now_utc // add two days to give the user some leeway in case of working offline, and in different TZ
      ) {
        return { role: this.settings.cachedRole, expiry_utc: this.settings.cachedRoleExpiry_utc };
      } else {
        return { role: AuthRole.Unknown, expiry_utc: 'never', subscriptionExpired: true };
      }
    }
    let freshRole = authRoleFromStrings(auth.roles);
    if (auth.subscriptionStatus === SubscriptionStatus.Waiting) {
      freshRole = AuthRole.Unknown; // here, it doesn't matter if it expired or not - the user is not paying!
    }
    if (this.settings.cachedRole !== freshRole || this.settings.cachedRoleExpiry_utc !== auth.licenseExpires_utc) {
      this.settings.cachedRole = freshRole;
      this.settings.cachedRoleExpiry_utc = auth.licenseExpires_utc;
      this.saveRememberedRoleInBackground();
    }

    if (this.settings.cachedRole === AuthRole.Paid &&
        roleAfterLicenceExpiry(this.settings.cachedRole, this.settings.cachedRoleExpiry_utc, now_utc) === AuthRole.Unknown) {
      return { role: AuthRole.Unknown, expiry_utc: 'never', subscriptionExpired: true };
    }

    // the server says this user holds no role, and the licence it reports has an
    // end date that has passed: their subscription ran out, so the Subscribe page
    // should say so rather than showing a bare paywall. This case only became
    // reachable once an empty roles list started being believed above; before
    // that it was absorbed by the remembered-role branch, which set this same
    // flag once the remembered expiry was two days past.
    //
    // Derived entirely from `auth`, which does not change for the life of the
    // login, so every call answers the same. It must NOT be derived from
    // `settings.cachedRole`, which this method overwrites a few lines above: the
    // first caller would then consume the flag and every later one — including
    // the Subscribe page, which the router reaches only after its own role read —
    // would see it already gone.
    //
    // A COACH-granted client is excluded because their own subscription did not
    // end; their coach's did, and they are told something different (#4393
    // Surface A). See design-docs/coach-subscription-lapse.md.
    if (freshRole === AuthRole.Unknown && auth.licenseType !== UserLicenseType.Coach &&
        auth.licenseExpires_utc && auth.licenseExpires_utc !== 'never' &&
        DateTime.fromISO(auth.licenseExpires_utc) < now_utc) {
      return { role: AuthRole.Unknown, expiry_utc: 'never', subscriptionExpired: true };
    }

    return { role: this.settings.cachedRole, expiry_utc: this.settings.cachedRoleExpiry_utc };
  }

  getSubscriptionStatus(): SubscriptionStatus {
    if (!this.couch) {
      return SubscriptionStatus.None;
    }
    if (this.couch.licenseExpires_utc < DateTime.utc().toISO()) {
      return SubscriptionStatus.None;
    }
    return this.couch.subscriptionStatus ?? SubscriptionStatus.None;
  }

  /** @deprecated Use getSubscriptionStatus() instead */
  getPayProSubscriptionStatus() {
    return this.getSubscriptionStatus();
  }

  async getMethodsForEmailAsync(email: string) {
    return fetchSignInMethodsForEmail(this.firebase.fbAuth, email);
  }

  // df-e30dd9684cf6: a warm resume reads global_common from this device before the CouchDB login,
  // so it is only safe once that store has finished a download here. the next cold boot sets the
  // flag again, and the boot after the download has finished is warm as before.
  // design-docs/warm-resume.md, "When the warm path is taken"
  private dropWarmResumeIfCatalogNotDownloaded() {
    const warmUid = AuthService.getWarmResumeUid();
    if (!warmUid) return;
    const mustWait = warmResumeMustWaitForCatalog({
      commonDbHasSyncedOnThisDevice: DatabaseWrapper.hasSyncedOnThisDeviceMarkerFor(localSharedDbBaseName(DatabaseWrapper.GLOBAL_COMMON_DB_ID, warmUid)),
      browserSaysDefinitelyOffline: this.onlineStateService.browserSaysDefinitelyOffline(),
    });
    if (!mustWait) return;
    console.log('Auth: global_common has not finished a download on this device yet — taking the cold start instead of the warm resume');
    AuthService.clearWarmResumeFlag();
  }

  // Warm-resume synthesis: emit the cached UserAuth through the normal login
  // pipeline so Phase A runs immediately. Follows the same post-login path as
  // onLoginStateChange's warm-resume branch (skip CouchDB login, emit user).
  // Must be synchronous — callers rely on `this.user` being set before the
  // Firebase onAuthStateChanged listener is registered.
  private applyCachedUserAuth(userAuth: UserAuth) {
    this.suppressNullAuth = false;
    this.wasEverLoggedIn = true;
    Sentry.setUser({ email: userAuth.email, id: userAuth.uid });
    console.log(`Auth: User ${userAuth.name} <${userAuth.email}> (${userAuth.uid}) logged in via ${userAuth.method} (cached)`);
    this.user = userAuth;
    this.mainAuthSubject.next(userAuth);
  }

  async onLoginStateChange(user: User) {
    console.log('Auth: Got login state change');

    if (this.ignoreLoginStateChange) {
      console.log('Auth: Ignoring login state change');
      return;
    }

    // idempotency — warm-resume may have already synthesized login for this UID
    // before Firebase's authStateReady() resolved (issue #3184). Skip re-emission.
    if (user && this.user?.uid === user.uid) {
      console.log('Auth: Login state already applied for this user, skipping re-emission');
      return;
    }

    if (!user) {
      // if OTP login is pending, don't emit null — wait for the real login
      if (this.suppressNullAuth) {
        console.log('Auth: OTP login pending, suppressing null auth emission');
        return;
      }

      // clear stale warm resume flag if user logged out elsewhere
      if (AuthService.getWarmResumeUid()) {
        console.log('Auth: Clearing stale warm resume flag (user is null)');
        AuthService.clearWarmResumeFlag();
      }

      // make sure couch is logged out first
      console.log('Auth: Couch logged out');
      this.couch = null;
      // notify couch-login listeners (consent re-evaluation, #4219)
      this.couchAuthSubject.next(null);
      // #3493: clear any pending retry so focus/online events post-logout don't
      // re-fire a login for the prior user (listeners stay installed; the
      // flag check inside retryCouchLoginAsync is the guard).
      this.couchLoginRetryNeeded = false;
      // Group 3: drop any in-flight stuck watchdog for the prior user's episode.
      this.clearCouchLoginStuckWatchdog();

      // and only then notify logout of main user
      console.log('Auth: User logged out');
      this.user = null;
      this.mainAuthSubject.next(null);
    } else {
      this.suppressNullAuth = false;
      this.wasEverLoggedIn = true;

      // use this user for bug reporting from now on
      Sentry.setUser({ email: user.email, id: user.uid });

      // SSO-only signins (Google/Apple) typically populate `displayName` ONLY on the
      // provider record, leaving the Firebase Auth root record's `displayName` empty
      // unless something explicitly synced it via `user:rename`. Fall back to the
      // first provider's displayName so every UserAuth carries a usable name —
      // downstream code (coach claim flow, notifications, badges) assumes it.
      const displayName = user.displayName
        || user.providerData?.find(p => !!p.displayName)?.displayName
        || '';

      // prepare user auth object
      let userAuth: UserAuth = {
        name: displayName,
        email: user.email,
        avatarUrl: user.photoURL || 'assets/imgs/default-avatar.jpg',
        uid: user.uid,
        method: extractLoginMethodFromFirebaseUser(user),
      };

      console.log(`Auth: User ${userAuth.name} <${userAuth.email}> (${userAuth.uid}) logged in via ${userAuth.method}`);

      // #4615: if this app launch already reported reaching the login page, the
      // boot window that `booting_time` measures has been running while a person
      // read that page and signed in. Hand the window to the sign-in here, which
      // is the earliest point in our own code after their action — so the couch
      // login below stays INSIDE what we report, and so does the warm branch just
      // after. The collector no-ops when nothing was reported yet, which is the
      // ordinary stored-session boot: that one keeps measuring from launch,
      // pre-login `auth` work included. See boot-timings.ts.
      bootTimings.restartWindowForSignIn();

      // warm resume: skip CouchDB login, emit user immediately (CouchDB login deferred to Phase B).
      // When cached `warm_resume_user_auth` is valid, `applyCachedUserAuth` runs first in
      // `initAsync` and the idempotency guard above short-circuits before we reach this branch;
      // we only arrive here when `warm_resume_uid` is set but the cached UserAuth was missing or
      // rejected by `getWarmResumeUserAuth`'s validators (malformed JSON, schema drift, etc.).
      const isWarmResume = AuthService.getWarmResumeUid() === user.uid;
      if (isWarmResume) {
        console.log('Auth: Warm resume detected — skipping CouchDB login, emitting user immediately');
        this.user = userAuth;
        this.mainAuthSubject.next(userAuth);
        return;
      }

      // IMPORTANT: First login to couch before notifying main auth
      // O1 (#3986): this serialized pre-emission login is the cold path's
      // `couchLogin` boot phase (S1) — the number the optimistic-cold-boot
      // decision was made from, and it came out NO at #4166 (this login stays
      // on the critical path). Marked at this call site only, so the direct
      // post-boot retry callers (focus/online listeners) never touch the boot
      // measurement. A rare post-boot re-fire of onLoginStateChange itself
      // would re-bracket the phase, which is harmless: booting_time has
      // already been emitted by then, and "latest attempt wins" is the
      // collector's documented contract. end() sits in a finally so an
      // unexpected throw still closes the phase (same pattern as the
      // warm-path bracket in app.component.ts).
      bootTimings.start(BootPhase.CouchLogin);
      let loginSuccess: boolean;
      try {
        loginSuccess = await this.attemptCouchLoginAsync(CouchLoginReason.BootLogin);
      } finally {
        bootTimings.end(BootPhase.CouchLogin);
      }

      // if that is not successful, check if we're offline
      if (!loginSuccess) {
        // Classify CloudFunctionHttpError 502/503 as transient — they're a
        // Cloud Run scale-up race, not a real server error. Without this, the
        // R3 (Bug 11) change that surfaces 5xx as CloudFunctionHttpError
        // (instead of the prior bare HTTP Error) would route post-retry
        // exhaustion to the fatalError 'server_error' branch instead of
        // offline-mode-with-retry.
        const isRecoverableErr = isRecoverableCouchLoginError(this.lastCouchLoginError);
        // if user was online at start and failure is NOT an expected/recoverable one, this is a real server error
        if (this.wasOnlineAtLoginStart && !isRecoverableErr) {
          console.log('Auth: CouchDB login failed while online');
          // set user so page guard doesn't redirect to LoginPage (which force-reloads and deletes databases)
          this.user = userAuth;
          await this.logging.fatalError({
            type: FatalErrorKind.ServerError,
            error: this.lastCouchLoginError?.message || 'couchLogin failed',
          });
          return;
        } else {
          // #3493: we've fallen back to offline mode but the user is logged in
          // locally. Set the retry-needed flag so the splash can surface a
          // Retry button and the focus/online listeners can auto-retry once
          // conditions improve. Without this, the splash hangs forever
          // because downstream cold-start code expects this.couch to be live.
          console.log(`Auth: CouchDB login failed (${isRecoverableErr ? 'recoverable' : 'offline'}), proceeding in offline mode`);
          this.couch = null;
          // notify couch-login listeners (consent re-evaluation, #4219)
          this.couchAuthSubject.next(null);
          this.couchLoginRetryNeeded = true;
          this.installCouchLoginRetryListeners();
          // Sentry triage v1.25.0 Group 3: the deferral itself is the expected,
          // recoverable offline path (auto-retry listeners + splash Retry button), so
          // we no longer report it as an error — that was the high-volume
          // CouchLoginDeferredToRetry noise. The couch_login_failure Mixpanel event
          // already fired upstream with the error name. Instead, arm a watchdog that
          // reports a one-shot CouchLoginStuckOffline only if the login stays deferred
          // while the user keeps waiting.
          this.armCouchLoginStuckWatchdog();
        }
      }

      // Set user first before emitting any observables
      this.user = userAuth;

      // Now emit both auth states together
      this.mainAuthSubject.next(userAuth);
    }
  }


  // Wait for Firebase to finish restoring the persisted session. Warm-resume
  // fast path (issue #3184) intentionally skips this await on boot so Phase A
  // renders immediately — but callers that need a real Firebase user (couch
  // login after coming online) must wait for Firebase to settle, otherwise
  // currentUser is null and couch auth fails.
  //
  // L2 (#3986): bound the wait. This used to be a bare
  // `await this.firebase.fbAuth.authStateReady()` with no timeout. #3904 bounded
  // only the cold-boot call (see BOOT_AUTH_SETTLE_TIMEOUT_MS usage above); these
  // resume-side callers — the warm Phase A pre-lockout recheck (#3793) in
  // app.component.ts and every offline->online flip in online-state.service.ts —
  // stayed unbounded, so the same accounts:lookup storm that wedges cold boot could
  // wedge warm-resume recovery. Delegate to the same bounded helper. On non-settle,
  // proceed anyway (the later onAuthStateChanged fire routes the user if Firebase
  // eventually settles) and surface a live signal tagged with the call `site` so we
  // can tell the two resume paths apart in Sentry.
  //
  // #6096: counts running time, like the cold-boot wait above.
  async firebaseReadyAsync(site: string, timeoutMs = BOOT_AUTH_SETTLE_TIMEOUT_MS): Promise<void> {
    await this.waitForAuthSettleAsync(site, timeoutMs);
  }

  // #6079: the bounded Firebase wait shared by the cold-boot path and firebaseReadyAsync.
  // Returns whether auth settled before the limit. On a timeout it reports what happened
  // during the wait, and keeps listening so a Firebase that settles afterwards is reported
  // with how late it was. design-docs/telemetry.md, "BootAuthStateReadyTimeout".
  private async waitForAuthSettleAsync(site: string, timeoutMs: number): Promise<boolean> {
    const m = await measureAuthStateSettleAsync(this.firebase.fbAuth, timeoutMs, realAuthSettleEnvironment(), lateByMs => {
      // the app carried on without Firebase and the auth listener has routed the user by now;
      // the timeout report above stays the record of the stall, this only says how long it was
      this.logging.trackSilentInfo('BootAuthStateSettledLate', {
        site,
        late_by_ms: lateByMs,
        present: this.firebase.fbAuth.currentUser != null,
      });
    });
    if (!m.settled) this.reportAuthSettleTimeout(site, timeoutMs, m);
    return m.settled;
  }

  // #6079: one report for every Firebase wait that did not settle, whether the limit fired
  // or authStateReady() rejected first; ended_by says which
  private reportAuthSettleTimeout(site: string, timeoutMs: number, m: AuthSettleMeasurement): void {
    console.warn(`Auth: Firebase auth did not settle (${m.end}, limit ${timeoutMs}ms, site=${site}) — proceeding (Firebase auth-init may be stuck)`);
    const context = {
      timeout_ms: timeoutMs,
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      online: navigator.onLine,
      app_native: isCapacitorNative(),
      site,
      ended_by: m.end,
      rejection: m.rejection,
      wait_wall_ms: m.waitWallMs,
      not_running_ms: m.notRunningMs,
      hidden_during_wait: m.hiddenDuringWait,
      page_age_ms: m.pageAgeMs,
      firebase_requests_available: m.firebaseRequests?.available ?? false,
      firebase_requests_buffer_entries: m.firebaseRequests?.bufferEntries ?? 0,
      firebase_requests: m.firebaseRequests?.requests ?? [],
      firebase_last_fetch_failure: m.firebaseLastFetchFailure,
    };
    // if the page was hidden during the wait, nobody watched a splash and the browser may have
    // throttled the page: an expected condition, so it is kept apart from the real stalls
    if (m.hiddenDuringWait) {
      this.logging.trackSilentInfo('BootAuthStateReadyTimeoutWhileHidden', context);
    } else {
      this.logging.trackSilentProblem('BootAuthStateReadyTimeout', context);
    }
  }

  // #3493: re-fire a deferred couchLogin once conditions improve (focus regain,
  // online event) or when the user taps the splash Retry button. No-op when
  // there's nothing pending. On success, re-emits on mainAuthSubject so the
  // app.component.ts:logonChangedAsync subscriber re-runs postLoginColdStartAsync
  // with this.couch now populated — the first cold-start that ran with couch=null
  // bailed at initializeRemoteDb (database-wrapper.ts:902); this second pass
  // proceeds. Lower layers are idempotent (initializeRemoteDb's existing
  // remoteDb!=null check at :911 short-circuits a double-init).
  //
  // In-flight guard prevents concurrent focus+online signals from double-firing
  // the success branch — without it, both invocations would dedup inside
  // attemptCouchLoginAsync (couchLoginInProgress) but each would still execute
  // their own success epilogue, re-emitting mainAuthSubject twice. The post-await
  // re-check guards against logout-during-retry: signOut nulls this.user and
  // clears couchLoginRetryNeeded; honoring those bails out before re-emitting.
  // `manual` is true when the user taps the splash Retry button (vs the automatic
  // focus/online listeners). Group 3: a manual retry that still fails while online is
  // the clearest "about to bail" signal, so it reports CouchLoginStuckOffline
  // immediately; an automatic retry only reports past the wait threshold + foreground.
  public async retryCouchLoginAsync(manual = false): Promise<boolean> {
    // concurrency dedup stays imperative — this is a mutex, not part of the decision.
    if (this.couchLoginRetryInFlight) return false;
    // L4 (#3986): the retry/skip decision now lives in the pure gate. `manual` (the splash
    // Retry tap) is the `force` flag: it lets a forced attempt proceed even when
    // couchLoginRetryNeeded was never armed (pre-deferral hang), and fall through to a
    // fresh login when the user has not been emitted yet (pre-login hang). The offline
    // bail stays for everyone.
    const action = resolveRetryCouchLoginGate({
      couchLoginRetryNeeded: this.couchLoginRetryNeeded,
      // "definitely offline" plumbing, not a feature gate (F9): only navigator.onLine === false is trusted here. isLikelyOnline() also
      // requires a completed probe, and the boot placeholder is Offline — using it would make the splash Retry button a no-op on an
      // actually-online device, regressing the L4 fix (#3986). See couch-login-retry-gate.ts.
      // eslint-disable-next-line no-restricted-syntax -- see the note above
      online: navigator.onLine,
      hasUser: this.user != null,
      force: manual,
    });
    if (action === 'skip-offline') {
      console.log('Auth: couch login retry skipped — offline');
      return false;
    }
    if (action === 'skip-not-needed') {
      return false;
    }
    // action is 'retry', or 'fresh-login' (a forced attempt with no emitted user yet —
    // attemptCouchLoginAsync (re)establishes the session from Firebase's currentUser).
    console.log(`Auth: retrying couch login (${action})`);
    this.couchLoginRetryInFlight = true;
    // a retry that succeeds re-emits the user, which runs the post-login boot again, and that
    // boot's navigation start-up sends the person through the page filter. claim the move and,
    // on success, hand the claim to that boot, which releases it once its start-up is over, so a
    // page that heard the record meanwhile does not move the person alongside it (df-53c89a39b0e6)
    const routeClaim = this.navCtrl.claimPostLoginRoute('login retry');
    let routeClaimHandedToBoot = false;
    try {
      const success = await this.attemptCouchLoginAsync(
        manual ? CouchLoginReason.SplashRetryManual : CouchLoginReason.DeferredRetryAuto);
      // re-check this.user after the await — logout could have raced ahead
      // while attemptCouchLoginAsync was in flight, in which case this.user
      // is null and we must not re-emit a stale userAuth. The retry-needed
      // flag is cleared by attemptCouchLogin_innerAsync's success branch, so
      // we don't gate on it here (gating on it would suppress the re-emit
      // entirely now that the inner-async clears it pre-return).
      if (success && this.user) {
        this.retryRouteClaimForNextBoot?.release();
        this.retryRouteClaimForNextBoot = routeClaim;
        routeClaimHandedToBoot = true;
        this.mainAuthSubject.next(this.user);
      } else if (!success) {
        // Group 3: the retry still failed — re-evaluate the stuck signal now (a
        // foreground refocus that keeps failing past the threshold, or a failed
        // manual Retry tap, reports here rather than waiting for the boot timer).
        this.maybeReportCouchLoginStuck(manual ? 'manual-retry' : 'auto-retry');
      }
      return success;
    } finally {
      this.couchLoginRetryInFlight = false;
      if (!routeClaimHandedToBoot) routeClaim.release();
    }
  }

  // #3493: install one-time window focus/online listeners that fire
  // retryCouchLoginAsync. Idempotent — called from the offline-fallback branch
  // in onLoginStateChange (which fires on network err, AbortError, or
  // offline-at-start). Listeners stay registered for the page lifetime
  // (AuthService is a singleton); cost of a no-op listener when
  // couchLoginRetryNeeded is false is negligible. Logout clears the flag so
  // post-logout focus/online events don't re-fire a login for the prior user.
  private installCouchLoginRetryListeners(): void {
    if (this.couchLoginRetryListenersInstalled) return;
    this.couchLoginRetryListenersInstalled = true;
    const onSignal = () => {
      // Group 3: if we're foreground again but still offline, retryCouchLoginAsync bails
      // at its offline guard before it can re-evaluate the stuck signal — and the boot
      // timer may have already fired (and skipped) while we were backgrounded. So
      // re-evaluate here while offline. When online, retryCouchLoginAsync runs and
      // reports the stuck signal itself on failure, so don't pre-empt it.
      // "definitely offline" plumbing, not a feature gate (F9): only navigator.onLine === false is trusted here. isLikelyOnline() also
      // requires a completed probe, and the boot placeholder is Offline — using it would make the splash Retry button a no-op on an
      // actually-online device, regressing the L4 fix (#3986). See couch-login-retry-gate.ts.
      // eslint-disable-next-line no-restricted-syntax -- see the note above
      if (!navigator.onLine) this.maybeReportCouchLoginStuck('foreground');
      void this.retryCouchLoginAsync();
    };
    window.addEventListener('focus', onSignal);
    window.addEventListener('online', onSignal);
  }

  // Sentry triage v1.25.0 Group 3: start (or keep) a one-shot watchdog for the current
  // deferred-login episode. Called when we enter offline mode with a pending retry. The
  // timer fires once after STUCK_DEFERRED_LOGIN_MS; if the login is still deferred and
  // the user is foregrounded, it reports CouchLoginStuckOffline. A successful login or a
  // sign-out clears the episode (clearCouchLoginStuckWatchdog).
  private armCouchLoginStuckWatchdog(): void {
    // if this is the first deferral of the episode, stamp its start (a re-deferral must
    // not reset the wait clock the user has already been enduring)
    if (this.couchLoginDeferredAt == null) {
      this.couchLoginDeferredAt = DateTime.utc();
      this.couchLoginStuckReported = false;
    }
    if (this.couchLoginStuckTimer != null) clearTimeout(this.couchLoginStuckTimer);
    this.couchLoginStuckTimer = setTimeout(() => {
      this.couchLoginStuckTimer = null;
      this.maybeReportCouchLoginStuck('timer');
    }, STUCK_DEFERRED_LOGIN_MS);
  }

  // Sentry triage v1.25.0 Group 3: decide (via the node-tested pure function
  // evaluateCouchLoginStuck) whether a still-deferred couch login has kept the user
  // waiting long enough — or a manual Retry just failed — to be worth a one-shot
  // CouchLoginStuckOffline report, and emit it if so.
  private maybeReportCouchLoginStuck(trigger: StuckEvalTrigger): void {
    if (this.couchLoginDeferredAt == null) return; // no active deferred episode
    const deferredElapsedMs = DateTime.utc().diff(this.couchLoginDeferredAt).as('milliseconds');
    const decision = evaluateCouchLoginStuck({
      retryStillNeeded: this.couchLoginRetryNeeded,
      deferredElapsedMs,
      foreground: this.focusState.focusState === FocusState.On,
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      online: navigator.onLine,
      alreadyReported: this.couchLoginStuckReported,
      trigger,
    });
    if (!decision.report) return;
    this.couchLoginStuckReported = true;
    const hadWarmResume = AuthService.getWarmResumeUid() != null;
    const stuckContext = {
      deferred_elapsed_ms: Math.round(deferredElapsedMs),
      trigger,
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      online: navigator.onLine,
      connection_state: this.onlineStateService.connectionState,
      was_online_at_login_start: this.wasOnlineAtLoginStart,
      cold_start: !hadWarmResume,
      last_error_name: this.lastCouchLoginError?.name,
    };
    this.telemetry.trackEvent('couch_login_stuck', stuckContext);
    // not the user's session going wrong: a login that cannot complete because the device
    // is offline is an ordinary loss of connectivity in an offline-first app, so it must
    // not stop the app asking this person for a rating.
    this.logging.trackSilentProblem('CouchLoginStuckOffline', stuckContext, undefined, SessionHealth.NotTheUsersSession);
  }

  // Sentry triage v1.25.0 Group 3: a deferred-login episode ended (login recovered or
  // the user signed out) — cancel the watchdog and reset its one-shot state so the next
  // episode starts clean.
  private clearCouchLoginStuckWatchdog(): void {
    if (this.couchLoginStuckTimer != null) {
      clearTimeout(this.couchLoginStuckTimer);
      this.couchLoginStuckTimer = null;
    }
    this.couchLoginDeferredAt = null;
    this.couchLoginStuckReported = false;
  }

  // predicate for "was this a sign-out race?" — true when the local UserAuth has been
  // cleared (onLoginStateChange(null)) or the Firebase user is gone (token revoked /
  // refresh failed). callers of attemptCouchLoginAsync that surface a Sentry error on
  // `false` (online-state, user-profile, the inner CloudFunctionAuthError canary, the
  // getFetchFunction slow-path relogin) must gate on this before reporting — otherwise
  // a benign sign-out lands in Sentry as a fake "auth failed". see design-docs/warm-resume.md
  // → Sign-out race convention.
  isLikelySignOutRace(): boolean {
    return !this.user || !this.firebase.fbAuth.currentUser;
  }

  // signing out is not the only benign way an in-flight request loses the session it was
  // started under: on a user SWITCH, onLoginStateChange replaces `this.user` with the NEW
  // user, so isLikelySignOutRace() is false and a report site gated only on it files a fake
  // "auth failed" for a request that belonged to the person who just left. A caller whose
  // request can outlive a switch captures `auth.user?.uid` before its first await and passes
  // it here. A request that began with nobody signed in (no uid) had no token to ask with,
  // so its failure is expected for the same reason. see design-docs/warm-resume.md
  // → Sign-out race convention.
  isLikelySessionChangeRace(uidAtStart: string | undefined): boolean {
    return this.isLikelySignOutRace() || !uidAtStart || this.user.uid !== uidAtStart;
  }

  // #4504: one-shot guard so a single hung login files ONE report, not one per waiting
  // request. Dozens of PouchDB fetches can be queued behind the same login deferred, and
  // when it hangs they all give up at nearly the same moment. Reset when the login episode
  // ends (see attemptCouchLoginAsync's finally), so the next hung login reports again.
  // Same one-shot shape as couchLoginStuckReported above, for the same reason.
  private couchLoginWaitTimeoutReported = false;

  // df-f59581ef5a04: the same one-shot guard for a give-up a phone freeze caused. Kept apart
  // from the flag above so a freeze does not use up the episode's problem report: a real hang
  // later in the same login episode must still report. Reset in the same finally.
  private couchLoginWaitFreezeReported = false;

  /**
   * #4504: report that a request stopped waiting for a hung couch login. Once per episode.
   *
   * df-f59581ef5a04: when the wait's time limit fired only because the phone froze the app
   * (the timer cannot run while the page is frozen, so it fires on waking and counts the
   * frozen minutes), this reports `CouchLoginWaitTimedOutAfterFreeze` as info instead. See
   * design-docs/telemetry.md for both names.
   */
  private reportCouchLoginWaitTimeout(waited: BoundedWaitResult<unknown>, attempt: number, loginAlreadyRefreshed: boolean): void {
    const context = {
      waited_ms: waited.waitedMs,
      timeout_ms: COUCH_LOGIN_WAIT_TIMEOUT_MS,
      not_running_ms: waited.notRunningMs,
      attempt,
      login_already_refreshed: loginAlreadyRefreshed,
      connection_state: this.onlineStateService.connectionState,
      app_native: isCapacitorNative(),
      last_error_name: this.lastCouchLoginError?.name,
    };
    // if a freeze caused the give-up, it is not a hang: say so once per episode, as info
    if (waited.freezeCaused) {
      if (this.couchLoginWaitFreezeReported) return;
      this.couchLoginWaitFreezeReported = true;
      // trackSilentInfo is allowed: the repair is already running — the fetch goes out next,
      // and PouchDB retries it and reports its own failures, so a freeze followed by a real
      // failure still shows. Same reasoning as the #6096 AfterFreeze reports.
      this.logging.trackSilentInfo('CouchLoginWaitTimedOutAfterFreeze', context);
      return;
    }
    if (this.couchLoginWaitTimeoutReported) return;
    this.couchLoginWaitTimeoutReported = true;
    // trackSilentProblem, not trackSilentInfo: nothing here proves a repair ran — the login
    // is still outstanding and we are proceeding in spite of it. Not trackAbnormalException
    // either: no exception was caught and the user is not interrupted, because the fetch
    // still goes out and PouchDB still owns the retry.
    this.logging.trackSilentProblem('CouchLoginWaitTimedOut', context);
  }

  /**
   * #4504: report a direct `/_session` refresh that threw instead of answering.
   *
   * df-c1ad475aa8f5: two causes are not a hang, and are kept out of `CouchSessionRefreshFailed`.
   * A phone that froze the app while the refresh was pending (on iOS it then fails on waking
   * with the phone's own "The request timed out.", not our timer) reports
   * `CouchSessionRefreshAfterFreeze` as info. A phone that is offline is only logged, as the
   * slow path below does. See design-docs/telemetry.md.
   */
  private reportSessionRefreshFailure(refresh: FailedNotingFreeze, attempt: number): void {
    const e = refresh.error as any;
    console.warn(`Auth: CouchDB direct refresh failed: ${e?.message}`);
    // if the phone froze the app while the refresh was pending, it is not a hang
    if (refresh.frozeWhilePending) {
      // trackSilentInfo is allowed: the repair runs next — the cloud-function login, which
      // reports its own failures — so a freeze followed by a real failure still shows
      this.logging.trackSilentInfo('CouchSessionRefreshAfterFreeze', {
        name: e?.name,
        message: e?.message,
        attempt,
        not_running_ms: Math.round(refresh.notRunningMs),
      });
    // if we're offline, a failed request is expected: log only
    } else if (this.onlineStateService.connectionState === ConnectionState.Offline) {
      console.log('Auth: direct refresh failed because offline — not reported, the cloud-function login runs next');
    // #4504: same guard the two warmup sites use, so an ordinary network blip stays quiet and
    // only a genuinely unexpected failure (a timeout among them) is reported
    } else if (!isTransientFetchError(e)) {
      this.reportUnlessHeldNetworkFailure(e, 'direct refresh', requestFields => {
        this.logging.trackSilentProblem('CouchSessionRefreshFailed', {
          name: e?.name,
          message: e?.message,
          attempt,
          ...requestFields,
        });
      });
    }
  }

  /**
   * df-7d52c9268cbf: file a `/_session` failure report, unless the failure was our own time
   * limit beating a fast network failure that the app-wide fetch wrapper was still holding.
   *
   * The wrapper checks whether the device is online before it hands a failed request back,
   * and that check can outlast the rest of the 10 s limit. The caller then sees a
   * TimeoutError for a request that had in fact failed at once because the server could not
   * be reached. For such a TimeoutError this waits in the background (bounded, see
   * `SESSION_REQUEST_OUTCOME_WAIT_MS`) for the request's real outcome. A network blip, or the
   * connection being Offline by then, is only logged; anything else is reported with fields
   * saying what the request did. The caller does not wait for any of this, so the login
   * moves on exactly when it did before. Any other error is reported at once, as before.
   */
  private reportUnlessHeldNetworkFailure(
    error: unknown, what: string, report: (requestFields: Partial<ReturnType<typeof sessionRequestOutcomeFields>>) => void,
  ): void {
    const outcome = sessionRequestOutcomeOf(error);
    // if our limit did not fire, the error is the request's own: report it now
    if (!outcome) {
      report({});
      return;
    }
    void outcome.then(settled => {
      // if the request had failed fast with a network blip, the timeout was ours, not CouchDB's
      if (wasHeldNetworkFailure(settled)) {
        console.log(`Auth: ${what} timed out while a network failure was being checked — not reported`);
      // if the wrapper's online check found the device offline, a failure is expected
      } else if (this.onlineStateService.connectionState === ConnectionState.Offline) {
        console.log(`Auth: ${what} timed out and the device is now offline — not reported`);
      } else {
        report(sessionRequestOutcomeFields(settled));
      }
    }).catch(err => this.logging.trackAbnormalException(err));
  }

  /**
   * Report a best-effort `/_session` cookie warmup after the couch login that threw instead of
   * answering. `method` is the warmup's HTTP method: POST is the couchPass path
   * (`CouchSessionPostFailed`), GET the legacy cookie path (`CouchSessionWarmupFailed`).
   *
   * df-e896bae83853: the same freeze split the direct refresh got in df-c1ad475aa8f5. A phone
   * that froze the app while the warmup was pending (on iOS it then fails on waking with the
   * phone's own "The request timed out.") reports `CouchSessionWarmupAfterFreeze` as info.
   * df-8612b571d195: a phone that is already known offline is only logged, as the direct refresh
   * does. Anything else reports exactly as before. See design-docs/telemetry.md.
   *
   * df-ff5b4cf38f54: both reports also carry the login's `reason` and the warmup's wall time
   * (`elapsed_ms`), so a hang right after a warm resume can be counted apart from a slow CouchDB.
   */
  private reportSessionWarmupFailure(
    warmup: FailedNotingFreeze, method: 'POST' | 'GET', reason: CouchLoginReason, elapsedMs: number,
  ): void {
    const e = warmup.error as any;
    const elapsed_ms = Math.round(elapsedMs);
    console.warn(`Auth: CouchDB ${method} _session warmup failed after ${elapsed_ms} ms (${reason}): ${e?.message}`);
    // if the phone froze the app while the warmup was pending, it is not a slow CouchDB
    if (warmup.frozeWhilePending) {
      // trackSilentInfo is allowed: the repair already runs — each request that waited on this
      // login refreshes the cookie at its retry (#6321), and that refresh reports its own failures
      this.logging.trackSilentInfo('CouchSessionWarmupAfterFreeze', {
        name: e?.name,
        message: e?.message,
        method,
        not_running_ms: Math.round(warmup.notRunningMs),
        reason,
        elapsed_ms,
      });
    // if we're offline, a failed warmup is expected — our own timeout among them: log only
    } else if (this.onlineStateService.connectionState === ConnectionState.Offline) {
      console.log(`Auth: ${method} _session warmup failed because offline — not reported, each waiting request refreshes the cookie at its retry`);
    // a transient offline / flaky-network failure is expected (the device went offline) and was
    // the dominant cause of the warmup noise, so only a genuinely unexpected failure (a timeout
    // among them) is reported (Sentry triage v1.25.0 Group 3)
    } else if (!isTransientFetchError(e)) {
      // read now, at the failure, even when the report waits for the request's real outcome
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      const online = navigator.onLine;
      this.reportUnlessHeldNetworkFailure(e, `${method} _session warmup`, requestFields => {
        this.logging.trackSilentProblem(method === 'POST' ? 'CouchSessionPostFailed' : 'CouchSessionWarmupFailed', {
          name: e?.name,
          message: e?.message,
          online,
          reason,
          elapsed_ms,
          ...requestFields,
        });
      });
    }
  }

  /**
   * #4504: the same bounded wait, for the relogin branches on the fetch's second attempt.
   *
   * They call `attemptCouchLoginAsync()`, which returns the SHARED login promise unchanged
   * when a login is already running — the very promise the wait at the top of the loop
   * bounds. Leaving these three bare would have made the protection asymmetric in exactly
   * the case the backstop exists for: a new unbounded await inside the login would be caught
   * at the top of the loop and still hang for ever here. Times out to `false`, which the
   * callers already treat as "the relogin did not help" and carry on from.
   */
  private async attemptCouchLoginBoundedAsync(attempt: number, loginAlreadyRefreshed: boolean): Promise<boolean> {
    const waited = await awaitBoundedLive(
      this.attemptCouchLoginAsync(CouchLoginReason.FetchRelogin), COUCH_LOGIN_WAIT_TIMEOUT_MS);
    if (!waited.timedOut) return waited.value === true;
    console.warn(`Auth: gave up waiting for Couch relogin after ${waited.waitedMs}ms — fetching anyway`);
    this.reportCouchLoginWaitTimeout(waited, attempt, loginAlreadyRefreshed);
    return false;
  }

  // df-6bc15f1a946e: identifies THIS page load. A storm that spans several of these is a
  // reload loop (what #4942 and #4994 both turned out to be); a storm carrying one is a loop
  // inside a single page. The two need fixes at different layers, and until now nothing in
  // the app could tell them apart. Minted once per JavaScript context, so a reload changes it.
  private static readonly bootContextId = Math.random().toString(36).slice(2, 10);

  // df-6bc15f1a946e: one-shot, so a device whose sessionStorage refuses us files ONE report
  // instead of one per login. Not re-armed: the condition is a property of the device, not of
  // a login episode, so a second report would say nothing new.
  private couchLoginStormStoreFailureReported = false;

  // df-6bc15f1a946e: a SECOND one-shot, deliberately not the flag above. The two conditions are
  // unrelated — storage refusing us is a property of the device, a throw in here is a bug in
  // this code — and one flag covering both means the commonest device (Safari private mode)
  // silently eats the report for the rarer, more serious one.
  private couchLoginStormDetectionThrewReported = false;

  // df-6bc15f1a946e: the in-memory fallback for the report cooldown, used only when
  // sessionStorage will not hold the persisted mark. Without it a device that reads but
  // refuses writes never records that it has reported, so every later login in the tab files
  // the same storm again — the exact flood the persisted mark exists to prevent. It cannot
  // replace the persisted mark, because it dies with the page and a reload storm is precisely
  // where that matters; it is the backstop for the one case the persisted mark cannot cover.
  private couchLoginStormLastReportedAtMs: number | null = null;

  /**
   * df-6bc15f1a946e: record that a couch login is starting, and report a storm if the record
   * now shows one.
   *
   * Deliberately best-effort and never allowed to affect the login: it runs before the login
   * and its whole job is to observe. A failure to store is REPORTED (once) rather than
   * swallowed, per CLAUDE.md's rule that unexpected conditions must surface.
   */
  private recordCouchLoginStartForStormDetection(reason: CouchLoginReason): void {
    try {
      const nowMs = Date.now();
      const recorded = recordCouchLoginStart(sessionStorage, {
        atMs: nowMs,
        reason,
        contextId: AuthService.bootContextId,
      });
      if (recorded.failure && !this.couchLoginStormStoreFailureReported) {
        this.couchLoginStormStoreFailureReported = true;
        this.logging.trackSilentProblem('CouchLoginStormStoreUnavailable', {
          failure: recorded.failure,
          app_native: isCapacitorNative(),
        });
      }
      const storm = resolveCouchLoginStormReport(recorded.state, nowMs);
      if (!storm) return;
      // if a report went out recently from THIS page and the mark did not persist, stay quiet
      if (this.couchLoginStormLastReportedAtMs != null
        && nowMs - this.couchLoginStormLastReportedAtMs < STORM_REPORT_COOLDOWN_MS) {
        return;
      }

      // mark it reported BEFORE sending. Same rule, and the same reason, as the handled
      // launch link in f453c1187: a send that navigates the page away loses a mark written
      // after it, and in a reload storm that would file one report per reload.
      const markFailure = markCouchLoginStormReported(sessionStorage, nowMs, storm.newestStartAtMs);
      // the in-memory cooldown is armed whatever happens, because it is what holds the line
      // when the write above did not stick
      this.couchLoginStormLastReportedAtMs = nowMs;
      if (markFailure && !this.couchLoginStormStoreFailureReported) {
        this.couchLoginStormStoreFailureReported = true;
        this.logging.trackSilentProblem('CouchLoginStormStoreUnavailable', {
          failure: markFailure,
          app_native: isCapacitorNative(),
        });
      }
      // trackSilentProblem, not trackSilentInfo: nothing has been repaired. The storm is
      // still happening (or just happened) and nobody has been interrupted about it.
      this.logging.trackSilentProblem('CouchLoginStorm', {
        starts_in_window: storm.startsInWindow,
        window_ms: storm.windowMs,
        span_ms: storm.spanMs,
        // more than one means the page kept reloading — the shape both measured storms had
        distinct_page_loads: storm.distinctContexts,
        reasons: storm.reasonCounts.map(r => `${r.reason}=${r.count}`).join(','),
        // how long ago the burst ended, so a retrospective report is not read as a live one
        reported_after_ms: nowMs - storm.newestStartAtMs,
        connection_state: this.onlineStateService.connectionState,
        app_native: isCapacitorNative(),
        app_standalone: isAppStandalone(),
      });
    } catch (err) {
      // observing must never break the thing it observes, so a fault here cannot travel into
      // the login. It still has to reach a debugger, hence the report rather than a bare catch.
      console.error('Auth: couch-login storm detection failed', describeErrorForLog(err));
      if (!this.couchLoginStormDetectionThrewReported) {
        this.couchLoginStormDetectionThrewReported = true;
        this.logging.trackSilentProblem('CouchLoginStormDetectionThrew', {
          message: describeErrorForLog(err),
        });
      }
    }
  }

  couchLoginInProgress: DeferredPromise<boolean> = null; // this is a state variable only for the function below
  // this will attempt to login to couch and if succeeded, it will broadcast the new auth
  // One can wait on this if needed, and it guarantees not to start several times in parallel - if one instance
  // is already started, the next one will just wait on the original to finish - won't start another authorization.
  // userAuth parameter: Pass the UserAuth object during initial login when this.user hasn't been set yet
  //
  // `reason` (df-6bc15f1a946e) says which caller is asking. It is required rather than
  // optional so a new call site cannot land without one, and it exists because twice in
  // production this method ran hundreds of times in minutes with nothing naming the caller
  // (#4942, #4994). It changes no behaviour: it is recorded and reported, never acted on.
  async attemptCouchLoginAsync(reason: CouchLoginReason): Promise<boolean> {
    // if already in progress, just return something to wait upon
    if (this.couchLoginInProgress != null) {
      return this.couchLoginInProgress;
    }

    // a NEW login is starting. Recorded here rather than above the dedup on purpose: a caller
    // that joins an in-flight login starts nothing, and counting it would inflate every burst.
    //
    // This counts login ATTEMPTS, not confirmed server calls, and the two are not the same:
    // attemptCouchLogin_innerAsync still bails without reaching the cloud function when the
    // device is offline or Firebase has no user. So a storm report can name a burst the server
    // logs will not corroborate — which is itself worth knowing, and is the reason the report
    // carries connection_state. It matches what `couch_login_attempt` counts, deliberately.
    this.recordCouchLoginStartForStormDetection(reason);

    // #3493: try/finally guarantees the deferred resolves and the slot clears
    // even if the inner async throws. Without this, an uncaught throw would
    // strand the deferred forever, and the early-return at line above would
    // deadlock every subsequent attempt. Phase B-1's focus/online auto-retry
    // exercises this path on every signal, so the hole had to be plugged.
    const deferred = DeferredPromise.defer<boolean>();
    this.couchLoginInProgress = deferred;
    let success = false;
    try {
      success = await this.attemptCouchLogin_innerAsync(reason);
      return success;
    } finally {
      this.couchLoginInProgress = null;
      // #4504: this login episode is over, so re-arm the one-shot wait-timeout report. A
      // later hung login is a new episode and must be reported on its own.
      this.couchLoginWaitTimeoutReported = false;
      this.couchLoginWaitFreezeReported = false;
      deferred.resolve(success);
    }
  }

  private wasOnlineAtLoginStart = false;

  /**
   * Emit the `couch_login_failure` Mixpanel event for one failure exit of
   * attemptCouchLogin_innerAsync. Every exit sends the same environment fields, so they
   * are filled in here once instead of being re-spelled (and kept in step) at each site —
   * the same shaping-helper pattern as TelemetryService.trackAppUpdateEvent.
   *
   * `reprobed` and `firebaseSettled` belong to one branch each (see design-docs/telemetry.md).
   * Keep them as named keys here rather than spreading in an `extras` bag: the
   * telemetry-lexicon gate reads this object literal as source text, and a spread would hide
   * those two names from it. A branch that leaves one unset sends `undefined`, which is
   * dropped when the event is serialized, so the property stays branch-only in the data.
   */
  private reportCouchLoginFailure(failure: {
    errorName: string;
    errorMessage: string;
    retryCount: number;
    reprobed?: boolean;
    firebaseSettled?: boolean;
  }): void {
    this.telemetry.trackEvent('couch_login_failure', {
      error_name: failure.errorName,
      error_message: failure.errorMessage,
      retry_count: failure.retryCount,
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      online_at_attempt: navigator.onLine,
      was_online_at_login_start: this.wasOnlineAtLoginStart,
      reprobed: failure.reprobed,
      firebase_settled: failure.firebaseSettled,
    });
  }

  async attemptCouchLogin_innerAsync(reason: CouchLoginReason): Promise<boolean> {
    console.log('Auth: Acquiring couch login');
    // #4010 review: reset the stored failure at the start of EVERY attempt, so
    // isLastCouchLoginErrorRecoverable() only ever describes the CURRENT
    // attempt. Without this, a recoverable error left over from an earlier
    // flap could demote a later, genuinely non-recoverable failure (e.g. the
    // auth-token-unavailable path, which returns false without storing an
    // error) to the silent recoverable branch in online-state.service.ts.
    // Accepted trade-off: the stuck-watchdog's couch_login_stuck telemetry
    // reads this field too (last_error_name); if a later attempt clean-bails
    // (OfflineAtStart / NoFirebaseUser / auth-token-unavailable) between
    // arming and firing, that field now reads undefined instead of the
    // original failure's name — correctness of the network-vs-auth
    // classification beats grouping fidelity on a diagnostic field.
    this.lastCouchLoginError = null;
    this.lastCouchLoginSkip = null;
    // #3493 Phase E: emit at attempt start so Mixpanel funnels can correlate
    // success / failure rates against environment (online state, standalone,
    // native shell, warm-resume path).
    this.telemetry.trackEvent('couch_login_attempt', {
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      online: navigator.onLine,
      app_standalone: isAppStandalone(),
      app_native: isCapacitorNative(),
      had_warm_resume: AuthService.getWarmResumeUid() != null,
      // df-6bc15f1a946e: which caller asked. This event already fires once per attempt and
      // already leaves the device, so it names the caller with no extra storage. It does not
      // replace the storm report: this queue may not flush inside the very short page life a
      // reload loop gives it, which is why the storm record is persisted separately.
      reason,
    });
    // Only attempt CouchDB login when online. #3878: the cached connection state can be a
    // stale false-offline — it only flips via a ping.svg probe that can latch offline at cold
    // start or on one transient failure even while the device is online (OfflineAtStart was
    // 165 users/30d, spread across all platforms with online_at_attempt: true). So before
    // bailing, do a fresh reachability re-probe and trust it over the stale latch.
    if (this.onlineStateService.connectionState !== ConnectionState.Online) {
      const reprobeReachable = await this.onlineStateService.probeServerReachableAsync();
      // #6231: read the state again after the re-probe. A device that came back online while it
      // ran has written Online, and the coming-online refresh then joins THIS login — see
      // resolveCouchLoginOnlineGate for why the newer answer wins.
      const gate = resolveCouchLoginOnlineGate({
        connectionStateOnline: false,
        reprobeReachable,
        connectionStateOnlineAfterReprobe: this.onlineStateService.isConnectionStateOnline(),
      });
      if (gate === 'bail') {
        console.log(`Auth: Browser is offline, cannot authenticate with CouchDB.`);
        this.lastCouchLoginSkip = { reason: CouchLoginSkipReason.OfflineAtStart };
        this.reportCouchLoginFailure({
          errorName: 'OfflineAtStart',
          errorMessage: 'connectionState !== Online',
          retryCount: 0,
          reprobed: true,
        });
        return false;
      }
      if (gate === 'proceed') {
        console.log('Auth: re-probe found the server unreachable, but the state went Online while it ran — proceeding');
      } else {
        // gate === 'force-online': the re-probe reached the server, so the cached offline was
        // stale. Correct it via forceOnline() — which is re-entrancy-safe (it sets the state
        // directly and does NOT call attemptCouchLoginAsync the way setConnectionStateAsync
        // would, which would deadlock on this in-flight login) — then proceed.
        console.log('Auth: cached state was offline but re-probe reached the server — forcing online');
        this.onlineStateService.forceOnline();
      }
    }
    this.wasOnlineAtLoginStart = true;

    // if no Firebase user is logged in, bail — the cloud function requires an auth token.
    // #3878: wait (bounded) for Firebase to finish restoring the session before reading
    // currentUser. On the warm-resume fast path (#3184) and other callers that don't
    // pre-await authStateReady(), currentUser is transiently null and we used to bail
    // spuriously — NoFirebaseUser was the largest couch_login_failure bucket (231 users/30d).
    // The 5s ceiling mirrors cloud-functions.service.ts's authStateReady race.
    // It stays on wall time (#6096): no report of it firing after a freeze was found, and
    // COUCH_LOGIN_CONSTRUCTION_CEILING_MS counts it in wall time.
    const firebaseUser = await awaitFirebaseUserPresentAsync(this.firebase.fbAuth, 5000);
    if (!firebaseUser.present) {
      console.log('Auth: No Firebase user, skipping couch login');
      this.lastCouchLoginSkip = { reason: CouchLoginSkipReason.NoFirebaseUser, firebaseSettled: firebaseUser.settled };
      this.reportCouchLoginFailure({
        errorName: 'NoFirebaseUser',
        errorMessage: 'firebase.fbAuth.currentUser is null',
        retryCount: 0,
        // firebaseSettled distinguishes "auth fully settled, still no user" (true — an
        // ordinary logged-out user OR a rejected/expired session such as the accounts:lookup
        // 400) from "still settling / hung" (false). It narrows, not pinpoints, the 400 subset.
        firebaseSettled: firebaseUser.settled,
      });
      return false;
    }
    
    /* Don't want to bail after first try, to give server enough time to register the user
     completely. Want to retry early if possible for better user experience. On the other hand, 
     don't want to DDoS ourselves in case of global issues. So do kind of "exponential back-off"
     */
    let retryDelays = [2000, 5000, 10000];
    let totalRetryTime = retryDelays.reduce((sum, d) => sum + d, 0);
    let retryProgressShown = false;
    let lastError: any;
    for (let delay of retryDelays) {
      try {
        // send where this person came from, so the server can record it on the
        // one-time create of their user document (#5408). It is ignored on every
        // later login, and a login never fails because of it — see
        // `CouchLoginParams.telemetryData`.
        let auth: CouchLoginResult = await this.cloudFunctionsService.couchLoginAsync({
          telemetryData: this.urlQuery.getTrackingDataForAccountCreationBase64(),
        });

        auth.userHex = asciiToHex(auth.user);
        // do NOT log auth.sessionCookie — it is the CouchDB AuthSession bearer token,
        // and the E2E harness captures the browser console into a CI artifact, so any
        // credential printed here would be published. Log the account identity only.
        console.log(`Auth: Got couch login ${auth.user}/${auth.userHex}:<redacted-session-token>`);
        console.log(`Auth: Couch user roles: ${auth.roles.join('+')}`);

        // set the fresh cookie for all the databases in the app to use
        // on Capacitor, clear HttpOnly cookies from native cookie jar first —
        // document.cookie can't touch HttpOnly cookies on native
        if (isCapacitorNative()) {
          // #4504: the bridge call takes no abort signal, so bound it by racing a timer.
          // A bridge that stops answering must not hold the login — and through it every
          // CouchDB request waiting on the login — open for ever. Carrying on without the
          // clear leaves a stale cookie, which the 401-refresh path below already handles.
          const cleared = await awaitBoundedLive(
            CapacitorCookies.clearCookies({ url: AppEnvironment.pouchConfig.couch_serverurl }),
            COOKIE_CLEAR_TIMEOUT_MS,
          );
          if (cleared.timedOut) {
            console.warn(`Auth: native cookie clear did not answer within ${COOKIE_CLEAR_TIMEOUT_MS}ms — continuing without it`);
            this.logging.trackSilentProblem('CouchCookieClearTimedOut', {
              waited_ms: cleared.waitedMs,
              site: 'couch-login',
            });
          }
        }

        if (auth.couchPass) {
          // POST directly to CouchDB _session — CouchDB authenticates from the body
          // and responds with a fresh HttpOnly Set-Cookie. On web, the browser handles
          // it natively. On Capacitor (CapacitorHttp enabled), URLSession handles it
          // identically — no CapacitorCookies.setCookie() bridge delay.

          // clear any stale JS domain cookie (web-only, harmless no-op on native)
          document.cookie = `AuthSession=; path=/; domain=${getRootDomain()}; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=none`;

          // #4504: bounded — a /_session call that hangs used to leave every CouchDB
          // request waiting on this login with no ceiling at all. The limit stays on wall
          // time; the clock beside it only says whether the phone froze the app meanwhile
          // (df-e896bae83853).
          const warmupStartedMs = performance.now();
          const warmup = await settleNotingFreezeAsync(() => fetchSessionEndpointAsync(
            fetch, AppEnvironment.pouchConfig.couch_serverurl + '_session', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: auth.user, password: auth.couchPass }),
            }));
          // POST /_session warmup is best-effort — couchLogin already succeeded via
          // the cloud function. The requests that waited on this login are refused once,
          // and each then refreshes the cookie at its retry (#6321 made sure a request
          // does not mistake this login for a refresh that worked).
          if (warmup.outcome === SettledOutcome.Failed) {
            this.reportSessionWarmupFailure(warmup, 'POST', reason, performance.now() - warmupStartedMs);
          } else {
            console.log(`Auth: CouchDB POST _session: ${warmup.value.status}`);
          }
        } else {
          // Fallback: set JS domain cookie + GET warmup (legacy path, no couchPass available)
          const cookieString =
            'AuthSession=' +
            auth.sessionCookie +
            `;path=/;domain=${getRootDomain()}` +
            ';expires=' +
            auth.sessionExpires +
            ';secure;samesite=none';
          document.cookie = cookieString;
          // #4504: bounded, and the freeze clock beside it, same reasons as the POST branch above.
          const warmupStartedMs = performance.now();
          const warmup = await settleNotingFreezeAsync(() => fetchSessionEndpointAsync(
            fetch, AppEnvironment.pouchConfig.couch_serverurl + '_session', {
              method: 'GET',
              credentials: 'include',
            }));
          // GET /_session warmup is best-effort on the legacy cookie path — same rationale
          // as the POST branch above
          if (warmup.outcome === SettledOutcome.Failed) {
            this.reportSessionWarmupFailure(warmup, 'GET', reason, performance.now() - warmupStartedMs);
          } else {
            console.log(`Auth: CouchDB warmup: ${warmup.value.status}`);
          }
        }

        this.couch = auth;
        // notify couch-login listeners (consent re-evaluation, #4219)
        this.couchAuthSubject.next(auth);
        // #4027: persist the non-secret session hint (who logged in + when the
        // cookie expires — NEVER the cookie value or a password). TWO things
        // read it, so this write is not optional bookkeeping: the NEXT boot's
        // booting_time reports `prior_session_hint` from it, and
        // `resolveBootLocale` treats its existence as "signed-in account" when
        // choosing the boot language (#4284). It was also going to trigger the
        // optimistic cold-boot path, but that was rejected at #4166 — see
        // couch-session-hint.ts's header. Skipped when Firebase's currentUser
        // is already gone (a
        // sign-out race) — a hint written after the sign-out wipe would leak a
        // stale identity marker into the next account on this browser.
        // The write is guarded: localStorage.setItem can throw (Safari private
        // mode QuotaExceededError, full quota), and the login has ALREADY
        // succeeded at this point — a storage failure must surface as a silent
        // problem, never fall into the retry loop's catch below (which would
        // re-run the successful login as if it had failed).
        try {
          const hintUid = this.firebase.fbAuth.currentUser?.uid;
          if (hintUid) {
            setCouchSessionHint({
              uid: hintUid,
              user: auth.user,
              userHex: auth.userHex ?? '',
              roles: auth.roles ?? [],
              sessionExpires: auth.sessionExpires,
            });
          }
        } catch (hintErr: any) {
          this.logging.trackSilentProblem('CouchSessionHintWriteFailed', {
            name: hintErr?.name,
            message: hintErr?.message,
          });
        }
        // #3493: any successful login path clears the retry-needed flag, not just
        // retryCouchLoginAsync — otherwise a session-refresh (getFetchFunction's
        // 401 retry path) that re-logs-in would leave the flag set, causing the
        // next focus/online event to fire a redundant retry.
        this.couchLoginRetryNeeded = false;
        // Group 3: the deferred-login episode recovered — stand down the stuck watchdog.
        this.clearCouchLoginStuckWatchdog();

        // if couchLogin succeeded but ConnectionStatus drifted to Offline during retries
        // (e.g., first attempt timed out triggering Offline, retry succeeded),
        // force it back to Online so database initial sync can proceed immediately
        if (this.onlineStateService.connectionState !== ConnectionState.Online) {
          this.onlineStateService.forceOnline();
        }

        return true;
      } catch (error) {
        // if no auth token available, bail immediately — retrying won't help
        if (error instanceof CloudFunctionAuthError) {
          // sign-out race: user signed out (or Firebase token revoked) between the
          // outer attemptCouchLoginAsync entry and this retry — token absence is the
          // expected consequence, not a bug. Demote to console.log, keep the canary
          // narrow to "should-have-had-a-token" cases (real refresh failure, clock
          // skew, server config drift).
          if (this.isLikelySignOutRace()) {
            console.log('Auth: Couch login skipped — user signed out during attempt (benign sign-out race)');
            return false;
          }
          console.error('Auth: Couch login failed — no auth token available, not retrying');
          this.lastCouchLoginSkip = { reason: CouchLoginSkipReason.AuthTokenUnavailable };
          // payload omits hasCurrentUser/user — both guaranteed truthy by the isLikelySignOutRace guard above
          this.logging.trackSilentProblem('CouchLoginAuthTokenUnavailable', {
            connectionState: this.onlineStateService.connectionState,
          });
          // #3493 Phase E: terminal failure (no retry) — mirror to Mixpanel for funnels
          this.reportCouchLoginFailure({
            errorName: 'CloudFunctionAuthError',
            errorMessage: error?.message ?? 'auth token unavailable',
            retryCount: 0,
          });
          return false;
        }
        // #4283: this server is running an older `_users` doc format version than this
        // account's data understands (UsersDocTooNewError, thrown server-side, surfaced
        // to the client as HTTP 409 — see declared-function-dispatch.ts). Retrying inside
        // this loop won't help — the server won't get newer in the next few seconds — so
        // bail now instead of hammering it 3 times with the generic
        // serverConnectionDifficulty() message. Unlike the CloudFunctionAuthError bail
        // above, DO store the error: the outer caller (onLoginStateChange) and
        // OnlineStateService both read `this.lastCouchLoginError` via
        // isRecoverableCouchLoginError to decide "treat as recoverable, not a real
        // server error" — leaving it unset would misclassify this as an unexplained
        // failure and route to the fatalError / Sentry-report branches instead.
        if (isVersionMismatchCouchLoginError(error)) {
          console.warn('Auth: Couch login failed — this server is behind this account\'s data version, not retrying immediately', error);
          this.lastCouchLoginError = error;
          // the zoneless app only repaints on an explicit markForCheck() — the sibling
          // generic branch below gets this for free from showRetryProgressBar(), which
          // this branch does not call (there is no bounded countdown to animate here),
          // so it must be requested directly or the message would be set but never shown
          getMyApp().syncRetryMessage = LL.app.serverBehindAccountVersion();
          getMyApp().markForCheck();
          // deliberately NOT LoggingService.trackSilentProblem() here — this condition
          // recurs on every focus/online auto-retry for as long as a live incident
          // lasts, which would be unbounded Sentry-adjacent volume (one event per user
          // per flip). The couch_login_failure Mixpanel event below, with its own
          // distinct error_name, is the intended observability channel for this case;
          // online-state.service.ts's coming-online refresh path makes the same choice
          // for the same reason.
          this.reportCouchLoginFailure({
            errorName: 'ServerDataVersionMismatch',
            errorMessage: error.message,
            retryCount: 0,
          });
          return false;
        }
        lastError = error;
        console.log('Auth: Couch login failure', error);
        console.log(`Auth: Couch auth failed. User not created yet? Retrying...`);
        // show a message and progress bar on the loading screen
        if (!retryProgressShown) {
          retryProgressShown = true;
          getMyApp().syncRetryMessage = LL.app.serverConnectionDifficulty();
          getMyApp().showRetryProgressBar(totalRetryTime);
        }
        await sleep(delay);
        continue;
      }
    }
    // only report non-recoverable failures to Sentry — network errors are transient
    // (device went offline). Cloud Run 502/503 (CloudFunctionHttpError) is a transient
    // scale-up race post-R3 (Bug 11), and a 409 (#4283, server behind this account's data
    // version) resolves itself once the live server catches up — treat both the same as a
    // network error: expected, not worth a silent-problem report.
    const isRecoverableErr = isRecoverableCouchLoginError(lastError);
    if (isRecoverableErr) {
      console.log(`Auth: Couch login - too many retries (recoverable)`, lastError?.message);
    } else {
      this.logging.trackSilentProblem(`Auth: Couch login - too many retries`, { lastError: lastError?.message || lastError?.status || 'unknown' });
    }
    this.lastCouchLoginError = lastError;
    // #3493 Phase E: retries exhausted — mirror to Mixpanel so funnels can see
    // the per-error-name distribution (AbortError vs CloudFunctionNetworkError
    // vs server-side) and the retry-count cohort.
    this.reportCouchLoginFailure({
      errorName: lastError?.name ?? 'Unknown',
      // coerce to string — lastError.status can be a number on HTTP/fetch errors,
      // and the design-doc types this property as string for Mixpanel.
      errorMessage: lastError?.message ?? String(lastError?.status ?? 'unknown'),
      retryCount: retryDelays.length,
    });

    return false;
  }

  // Creates a closure used to fetch from the remote database, with retrying if authentication fails
  getFetchFunction() {
    /* NOTE:
    The cookie could expire in the browser, but the server will also stop accepting it after it has expired due to small time synchronization variations (or outright wrong clocks), we can be in a situation where the browser has not yet expired the cookie, but the server rejects it.

    So we handle both the case where we know in advance that we need to refresh (cookie is locally gone) before fetching and the case where we have a cookie and send it, but it is rejected so we have to refresh and retry the fetch. 
     */

    // per-request timeout: above CouchDB longpoll cycle (~60s), below LB idle timeout (120s)
    const FETCH_TIMEOUT_MS = 90_000;

    // the ordinary attempts: first try, then one retry after refreshing the session
    const ORDINARY_ATTEMPTS = 2;
    // df-c70dd5850b6f: one attempt beyond those, taken ONLY for CouchDB's self-healing
    // `_users`-conflict 401 — see couch-fetch-conflict-retry.ts for why it is worth taking
    // and why it costs nothing on any other request
    const MAX_ATTEMPTS = ORDINARY_ATTEMPTS + 1;

    let fetchFunction = async (url: string, opts: any) => {
      opts.credentials = 'include';

      // tracks whether a parallel request already refreshed the session while we waited
      let loginAlreadyRefreshed = false;

      // has the one extra `_users`-conflict attempt been spent? it is allowed once per
      // request, so a document that stays conflicted cannot hold a request here for ever
      let conflictRetryTaken = false;

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {

        // wait for any eventual in-progress login from parallel requests.
        // #4504: bounded. This used to be a bare await, and it sits BEFORE the
        // AbortController below, so a login that never settled left this request with no
        // ceiling at all — the 90 s the function advertises covered only the fetch. On the
        // time limit we do NOT fail: we carry straight on to the fetch, for the same reason
        // the relogin branch below refuses to break early — the caller is PouchDB and it
        // needs a real response to retry on, and the login we gave up waiting for may well
        // have been a proactive refresh while the cookie we already hold is still good.
        if (this.couchLoginInProgress) {
          console.log('Auth: waiting Couch login to complete...');
          const waited = await awaitBoundedLive(this.couchLoginInProgress, COUCH_LOGIN_WAIT_TIMEOUT_MS);
          if (waited.timedOut) {
            console.warn(`Auth: gave up waiting for Couch login after ${waited.waitedMs}ms — fetching anyway`);
            this.reportCouchLoginWaitTimeout(waited, attempt, loginAlreadyRefreshed);
          } else if (waited.value) {
            loginAlreadyRefreshed = true;
          }
        }

        // if auth failed on first attempt, refresh the session before retrying
        if (attempt > 0) {
          // if user is signed out, abort the fetch — PouchDB handles AbortError gracefully
          if (!this.user) {
            console.log('Auth: fetch cancelled — user signed out during retry');
            throw new DOMException('User signed out during fetch', 'AbortError');
          }

          // if another request's login finished while we waited at the top of THIS attempt, reuse
          // its cookie instead of refreshing (on app resume, multiple DBs get 401 at once — they
          // share the one cloud-function login). The direct `_session` POST below is not shared:
          // each refused request that reaches it sends its own.
          if (loginAlreadyRefreshed) {
            console.log('Auth: session already refreshed by parallel request, retrying fetch...');
          // fast path: if we have the CouchDB password, POST directly to _session. NOT for the
          // extra `_users`-conflict attempt: a direct POST answers 200 while the document is
          // still conflicted and repairs nothing, so the retry would meet the same refusal.
          // Only the cloud-function login (the slow path below) runs the server's repair.
          } else if (this.couch?.couchPass && !conflictRetryTaken) {
            console.log('Auth: session expired, refreshing via direct POST...');
            // #4504: the relogin fallback runs AFTER the refresh is classified, not inside a
            // catch around it. Inside, a relogin that threw would be reported as
            // `CouchSessionRefreshFailed` — pointing a debugger at the session refresh when
            // the refresh had answered fine and the LOGIN was what failed — and the relogin
            // would then be attempted a second time.
            let refreshFailed = false;
            const { user: couchUser, couchPass } = this.couch;
            // #4504: bounded. This one is the same defect INSIDE the very function that
            // advertises the 90 s ceiling — it runs before the AbortController below, and
            // it is not reached through attemptCouchLoginAsync, so bounding the login
            // alone would have left it open. The limit stays on wall time; the clock beside
            // it only says whether the phone froze the app while it was pending.
            const refresh = await settleNotingFreezeAsync(() => fetchSessionEndpointAsync(
              fetch, AppEnvironment.pouchConfig.couch_serverurl + '_session', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: couchUser, password: couchPass }),
              }));
            if (refresh.outcome === SettledOutcome.Failed) {
              this.reportSessionRefreshFailure(refresh, attempt);
              refreshFailed = true;
            } else if (refresh.value.ok) {
              console.log(`Auth: CouchDB session refreshed: ${refresh.value.status}`);
            } else {
              console.log(`Auth: CouchDB direct refresh failed (${refresh.value.status}), falling back to cloud function`);
              refreshFailed = true;
            }
            if (refreshFailed) {
              await this.attemptCouchLoginBoundedAsync(attempt, loginAlreadyRefreshed);
            }
          } else {
            // slow path: call cloud function to get fresh credentials (and, on the extra
            // `_users`-conflict attempt, to have the server repair the document)
            console.log(conflictRetryTaken
              ? 'Auth: login record conflicted, trying Couch login so the server repairs it...'
              : 'Auth: session expired, trying Couch login...');
            let loginSuccess = await this.attemptCouchLoginBoundedAsync(attempt, loginAlreadyRefreshed);
            if (!loginSuccess) {
              // if we're offline
              if (this.onlineStateService.connectionState === ConnectionState.Offline) {
                // expected to fail, just log to console
                console.log('Auth: Cannot relogin because offline.');
              // if user signed out (or Firebase token revoked) during the await above,
              // it's a benign sign-out race — see design-docs/warm-resume.md
              // "Sign-out race convention"
              } else if (this.isLikelySignOutRace()) {
                console.log('Auth: session refresh skipped — user signed out during attempt (benign sign-out race)');
              // online and still signed in but the relogin failed — this is a real problem
              } else {
                this.logging.trackSilentProblem('Session expired, cannot relogin', 'CouchDB authentication');
              }
              /*
              Note: We don't break here! Because we need to call fetch to be able to return its failed result.
              */
            }
          }
        }

        // per-request timeout prevents hung connections from exhausting the browser's connection pool
        const controller = new AbortController();
        const fetchStartTime = Date.now();
        let timedOut = false;
        const timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, FETCH_TIMEOUT_MS);

        // chain with PouchDB's own cancellation signal (used by cancelSync)
        const originalSignal = opts.signal;
        if (originalSignal) {
          if (originalSignal.aborted) {
            clearTimeout(timeoutId);
            throw originalSignal.reason || new DOMException('Aborted', 'AbortError');
          }
          originalSignal.addEventListener('abort', () => controller.abort(), { once: true });
        }
        opts.signal = controller.signal;

        try {
          // make the fetch call
          let response: Response = await PouchDB.fetch(url, opts);
          clearTimeout(timeoutId);

          // if this is the first try, and the failure was due to missing/wrong cookie
          let failedAuth = (response.status == 401 || response.status == 403);
          if (attempt == 0 && failedAuth) {
            // retry with authentication
            console.log('Auth: fetch failed with auth error, will retry with Couch login...');
            // #6321: forget any login this request waited on above. It finished BEFORE this fetch
            // was sent, and the refusal shows it left no working cookie (its `_session` POST can
            // drop while the login still succeeds), so the next attempt must refresh rather than
            // reuse it. A login still running when the next attempt starts sets the flag again at
            // the top of the loop, which is wanted: many databases refused at once share one login.
            loginAlreadyRefreshed = false;
            opts.signal = originalSignal;
            continue;
          }

          // df-c70dd5850b6f: a 401 that survived the refresh-and-retry above may be the
          // self-healing `_users` conflict rather than a real authentication failure. Ask
          // ONLY here — a successful fetch never reads a body, and the first attempt's
          // 401 already continued above, so no request that is not twice-refused pays for
          // this check.
          if (response.status === 401 && await isUsersDocConflictResponseAsync(response)) {
            if (!conflictRetryTaken) {
              conflictRetryTaken = true;
              // forget the refresh we already did: it may have completed BEFORE the server
              // finished repairing the document, and reusing it would make the extra
              // attempt a bare re-fetch against a document nobody has repaired since.
              // This does not GUARANTEE a second login — the top of the next iteration
              // sets the flag again if a parallel attemptCouchLoginAsync resolves true
              // while we wait. That case is the good one and must not be "fixed": a
              // cloud-function login only succeeds when CouchDB accepted the account, so
              // the document was unconflicted at that moment, and reusing that session is
              // what stops a login storm during exactly the concurrent first sync this
              // recovery is for.
              loginAlreadyRefreshed = false;
              const waitMs = usersDocConflictRetryWaitMs(Math.random());
              console.log(`Auth: CouchDB refused the login record as conflicted (it repairs itself) — waiting ${waitMs}ms, then one more attempt`);
              opts.signal = originalSignal;
              await awaitUnlessAbortedAsync(waitMs, originalSignal);
              continue;
            }
            // the extra attempt was spent and the document is STILL conflicted. Nothing
            // reported this before, so a repair loop that keeps losing was invisible.
            this.logging.trackSilentProblem('CouchUsersDocConflictRetryExhausted', {
              urlPath: url.replace(/https?:\/\/[^/]+/, ''),
              method: opts?.method || 'GET',
            });
          }

          // log successes on second attempt - so we know the retry worked when reading the log
          if (attempt > 0 && response.status >= 200 && response.status < 300) {
            console.log(`Auth: fetch for ${url} succeeded after retry`);
          }

          return response;
        } catch (err: any) {
          clearTimeout(timeoutId);

          const elapsedMs = Date.now() - fetchStartTime;
          const urlPath = url.replace(/https?:\/\/[^/]+/, '');

          // log detailed context for AbortErrors to distinguish timeout vs sync cancellation
          if (err.name === 'AbortError') {
            const abortSource = timedOut ? 'timeout' : originalSignal?.aborted ? 'sync-cancel' : 'unknown';
            console.warn(`Auth: fetch aborted [${abortSource}] after ${elapsedMs}ms for ${urlPath}, ` +
              `connection=${this.onlineStateService.connectionState}, hidden=${document.hidden}`);
          } else {
            // log non-abort fetch errors (e.g. iOS "Load failed") for Sentry breadcrumbs
            const method = opts?.method || 'GET';
            console.log(`Auth: fetch error [${err.name}: ${err.message}] ${method} ${urlPath} after ${elapsedMs}ms, ` +
              `connection=${this.onlineStateService.connectionState}, hidden=${document.hidden}, ` +
              // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
              `online=${navigator.onLine}, errKeys=${Object.keys(err).join(',') || 'none'}`);
          }

          opts.signal = originalSignal;
          throw err;
        }
      }

      /*
        It shouldn't be possible to reach this point: every iteration of the loop above
        either returns a response or continues, and only the `_users`-conflict branch can
        continue past the ordinary attempts — and it may do so once per request.
      */
      this.logging.trackAbnormalError('ASSERTION FAILED: Internal fetch error');
      return null;
    };

    return fetchFunction;
  }

  // obtains couch data for a user, by user's email.
  // NOTE: the caller auth must have officer access!
  //
  // #4283 version affinity, client-side half: this reads the `_users` database
  // straight from the browser, so the server's own version guard never sees the
  // document. A build that does not understand the stored shape must refuse it
  // rather than hand back fields it may be misreading — see
  // design-docs/users-doc-versioning.md and util/users-doc-version-guard.ts.
  async obtainCouchUserInfoForUserAsync(email: string): Promise<CouchUserInfo> {
    // connect to the remote database
    let allUsers = await this.getAllUsersDbAsync();
    let foundUser: CouchUserInfo;
    try {
      let results = await allUsers.findViaIndexAsync({
        selector: {
          email: email,
        },
      });
      if (results.docs.length <= 0) {
        return null;
      }
      foundUser = results.docs[0];
    } catch (error) {
      if (isPouchNotFound(error)) {
        return null;
      }
      console.error("Auth: can't get couch user object", error);
      throw error;
    }
    // the check sits outside the try above on purpose: a refusal is not a failed
    // database read, and letting it fall into that catch would log it under the
    // wrong message before rethrowing it anyway.
    assertUsersDocVersionSupportedOnClient(foundUser, email);
    return foundUser;
  }

  // obtains couch data for all users that don't have the current product generation
  // NOTE: the caller auth must have officer access!
  //
  // #4283 version affinity, client-side half — same reasoning as
  // obtainCouchUserInfoForUserAsync above. One document this build cannot read
  // fails the whole batch: the caller's next step is to change and write these
  // documents back, and quietly dropping the ones we do not understand would
  // make that batch silently incomplete.
  async findUsersByOldProductGeneration(currentProductGeneration: number): Promise<CouchUserInfo[]> {
    // connect to the remote database
    let allUsers = await this.getAllUsersDbAsync();
    let returnedUsers: CouchUserInfo[];
    try {

      let results = await allUsers.runQueryAsync('usersByProductGeneration', undefined, {
        startkey: null,
        endkey: currentProductGeneration - 1,
        limit: 400,
        include_docs: true,
      });

      if (results.length <= 0) {
        return null;
      }
      returnedUsers = results.map(r => r.doc);
    } catch (error) {
      if (isPouchNotFound(error)) {
        return null;
      }
      console.error("Auth: can't get couch user objects", error);
      throw error;
    }
    // for each returned user document, refuse any shape this build cannot read.
    // this runs on EVERY returned document, and before any field of them is read:
    // `payProData` below is exactly the kind of field a format bump can move, so
    // filtering first would throw a TypeError and hide the real reason.
    for (let user of returnedUsers) {
      assertUsersDocVersionSupportedOnClient(user, user.email || user.name);
    }
    // don't return the trial users, they can't be remapped
    return returnedUsers.filter((u: CouchUserInfo) => !u.payProData.trialEnds_utc);
  }

  // Save couch data for a user - the object must be full info originating from obtainCouchUserInfoForUser().
  // NOTE: the caller auth must have officer access!
  //
  // #4283 version affinity, client-side half. This is the one client-side
  // `_users` WRITE path, so it is also the last place to stop a build that does
  // not understand the stored shape from overwriting it with a misread copy.
  // The read methods above already refuse, but this guard is kept here too: a
  // future caller could obtain the document some other way.
  async writeCouchUserInfoForUserAsync(user: CouchUserInfo) {
    assertUsersDocVersionSupportedOnClient(user, user?.email || user?.name);
    // connect to the remote database
    let allUsers = await this.getAllUsersDbAsync();
    try {
      await allUsers.saveRecord_rawAsync(user);
      console.log('Auth: Updated user info: ', user);
    } catch (error) {
      console.error("Auth: can't write couch user object", error);
      throw error;
    }
  }

  async signInWithEmailAsync(credentials: { email: string; password: string }): Promise<any> {
    console.log('Auth: Starting sign-in using email & password');
    return signInWithEmailAndPassword(this.firebase.fbAuth, credentials.email, credentials.password);
  }

  async signUpAsync(credentials: { email: string; displayName: string; password: string }): Promise<User> {
    try {
      this.ignoreLoginStateChange = true;
      console.log('Auth: Starting sign-up');
      let userCred = await createUserWithEmailAndPassword(this.firebase.fbAuth, credentials.email, credentials.password);
      let user = userCred.user;
      console.log(`Auth: User ${user.email} signed up as ${user.uid}`);
      await updateProfile(user, {
        displayName: credentials.displayName,
        photoURL: user.photoURL,
      });
      console.log(`Auth: Set name to ${user.displayName} for ${user.uid}`);
      this.ignoreLoginStateChange = false;
      await this.onLoginStateChange(user);
      return user;
    } finally {
      this.ignoreLoginStateChange = false;
    }
  }

  async resetPasswordAsync(email: string) {
    return sendPasswordResetEmail(this.firebase.fbAuth, email);
  }

  async signOutAsync(): Promise<void> {
    // wipe all user-scoped localStorage entries so the next account on the
    // same browser cannot inherit them. previously a per-key removeItem
    // approach, which silently leaked any new key (e.g. AutoPlanFoodsCached
    // turned meal-plan into blank rows for testers reusing browsers).
    // device-level + SDK-prefixed keys are explicitly preserved.
    clearUserScopedLocalStorage();
    // route warm-resume invalidation through the canonical helper to preserve
    // the invariant documented in design-docs/warm-resume.md: every clear
    // site calls clearWarmResumeFlag, and every signOut path is a clear site.
    AuthService.clearWarmResumeFlag();

    // reset the in-memory SettingsService to defaults before persisting —
    // otherwise saveAsync would re-write the previous user's full settings
    // back to localStorage, undoing the wipe for the broadest user-scoped key.
    // detach the database sync first. a server login still running in the background (Manage
    // Subscription starts one) can finish after this reset and save the settings again; with the
    // callback attached, that save copied every default into the user's still-open database and
    // overwrote their synced settings on the server. the next sign-in installs it again
    // (installSettingsSyncCallback in app.component.ts).
    this.settings.setSyncCallback(null);
    await this.settings.resetToDefaultsAsync(true);

    // invalidate CouchDB session and clear its HttpOnly cookie
    try {
      // #4504: bounded like every other /_session call. A hang here is worse than the
      // others, because the user is waiting on a sign-out they asked for; the catch below
      // already treats a failure as "the session will expire on its own".
      await fetchSessionEndpointAsync(
        fetch, AppEnvironment.pouchConfig.couch_serverurl + '_session', {
          method: 'DELETE',
          credentials: 'include',
        });
      console.log('Auth: CouchDB session deleted');
    } catch (e: any) {
      // if offline, the session will expire on its own
      console.log('Auth: CouchDB session delete failed:', e.message);
      // #4504: the call can now time out, and a hang here is not the offline case this
      // catch was written for — it means the sign-out sat waiting on a server that never
      // answered. Same isTransientFetchError guard as every other /_session site, so the
      // ordinary offline sign-out stays quiet.
      // df-7d52c9268cbf: a timeout that was really a held network failure stays quiet too.
      if (!isTransientFetchError(e)) {
        this.reportUnlessHeldNetworkFailure(e, 'session delete', requestFields => this.logging.trackSilentProblem('CouchSessionDeleteFailed', {
          name: e?.name,
          message: e?.message,
          ...requestFields,
        }));
      }
    }

    // on Capacitor native, also clear HttpOnly cookies from the native cookie jar
    // (document.cookie can't clear HttpOnly cookies — they persist across WebView reloads
    //  and cause 401 errors on the next login's sync requests)
    if (isCapacitorNative()) {
      try {
        const url = AppEnvironment.pouchConfig.couch_serverurl;
        // #4504: bounded — a bridge that stops answering must not hold up a sign-out.
        const cleared = await awaitBoundedLive(CapacitorCookies.clearCookies({ url }), COOKIE_CLEAR_TIMEOUT_MS);
        if (cleared.timedOut) {
          console.warn(`Auth: native cookie clear did not answer within ${COOKIE_CLEAR_TIMEOUT_MS}ms during sign-out — continuing`);
          this.logging.trackSilentProblem('CouchCookieClearTimedOut', {
            waited_ms: cleared.waitedMs,
            site: 'sign-out',
          });
        } else {
          console.log('Auth: Cleared native cookies for CouchDB');
        }
      } catch (e: any) {
        // the sign-out carries on either way, but a cookie left in the native jar is the one
        // that causes 401s on the next login's sync — the thrown twin of the timeout above
        this.logging.trackSilentProblem('CouchCookieClearFailed', {
          ...errorReportFields(e),
          site: 'sign-out',
        });
      }
    }

    // #4981 phase 4 (#5270): a COMPLETED sign-out puts the SQLite grace period back to the
    // beginning, and this is the last thing that happens before the sign-out returns.
    //
    // **This is deliberately not a contradiction of the counter surviving both localStorage
    // wipes, and both halves have to stay.** The survivor lists exist so a PARTIAL teardown
    // — the bounded one that may die before it finishes, or the fatal-error "start this
    // device over" reset — does not silently zero the counter and hand the device a fresh
    // grace period it has already served. A sign-out that runs to here is not partial: the
    // next person on this device has served no grace period for whatever SQLite copy they
    // end up with, so the count starts again.
    //
    // Reported rather than absorbed on failure: a counter that survives when it should have
    // been reset is the direction that costs somebody their data.
    try {
      localStorage.removeItem(SQLITE_READY_BOOTS_KEY);
    } catch (e: any) {
      this.logging.trackSilentProblem('AndroidSqliteReadyBootSignOutResetFailed', {
        name: e?.name,
        message: e?.message,
      });
    }

    return signOut(this.firebase.fbAuth);
  }

  /**
   * What a finished social sign-in tells us about the account it landed on (#4936).
   *
   * Both facts are read from the sign-in result itself rather than from
   * `this.user`, because `this.user` is filled in later by Firebase's
   * auth-state listener — so right after a sign-in it can still be empty or
   * still hold the PREVIOUS account. The result object is populated by the time
   * the sign-in call resolves, which is the moment the caller asks.
   *
   * This lives here so the Firebase import stays in the one file that already
   * owns Firebase, and all three social sign-in methods below hand back the
   * same kind of result, so one reader serves Apple, Google and Facebook.
   */
  describeSocialSignIn(credential: unknown): { accountWasJustCreated: boolean; signedInEmail: string | null } {
    try {
      const cred = credential as UserCredential;
      return {
        accountWasJustCreated: getAdditionalUserInfo(cred)?.isNewUser === true,
        signedInEmail: cred?.user?.email ?? null,
      };
    } catch (err) {
      // a result we cannot read answers "nothing was created", which is the
      // quiet direction on purpose: the only thing this drives is whether the
      // app asks the user an extra question, so guessing the other way would
      // put that question in front of people whose sign-in worked perfectly.
      // Firebase hands back a readable result for every sign-in that resolved, so an
      // unreadable one is unexpected and must reach a debugger (#5191)
      this.logging.trackSilentProblem('SocialSignInResultUnreadable', errorReportFields(err));
      return { accountWasJustCreated: false, signedInEmail: null };
    }
  }

  /**
   * The address of the account Firebase has signed in right now, or null.
   *
   * Read from Firebase rather than `this.user`, for the reason given on
   * `describeSocialSignIn`: right after a sign-in `this.user` can still be empty
   * or still hold the previous account.
   */
  getSignedInFirebaseEmail(): string | null {
    return this.firebase.fbAuth.currentUser?.email ?? null;
  }

  /**
   * `loginHint` preselects that account in Google's chooser (#5895). Web only: the
   * native Google plugin has no way to pass one. It relies on the web sign-in being
   * a POPUP, which keeps this page and the in-memory hint alive; switching to a
   * redirect sign-in would lose the hint.
   */
  async signInWithGoogleAsync(opts?: { loginHint?: string | null }): Promise<any> {
    console.log('Auth: Starting sign-in using Google OAuth');
    // if native Capacitor, use OS-level Google account picker
    if (isCapacitorNative()) {
      // S4 (#3986): the plugin init runs concurrently with boot now — make
      // sure it has finished before the first login call (it usually has,
      // long before a human can tap the button)
      await getMyApp().whenSocialLoginReadyAsync();
      let res;
      try {
        res = await SocialLogin.login({ provider: 'google', options: {} });
      } catch (err: any) {
        console.error('Auth: SocialLogin.login() failed:', describeErrorForLog(err));
        // user cancelled the account picker
        if (err?.message?.includes('cancel') || err?.code === 'SIGN_IN_CANCELED' || err?.code === '12501') {
          const cancelError = new Error('Sign-in cancelled') as any;
          cancelError.code = 'auth/popup-closed-by-user';
          throw cancelError;
        }
        throw err;
      }
      // never log `res` itself: it holds the Google tokens, email and name (df-20b2ac3b725f)
      console.log('Auth: SocialLogin result:', describeSocialLoginResultForLog('google', res));
      const googleResult = res.result as { idToken: string | null };
      if (!googleResult?.idToken) {
        throw new Error('Auth: Google Sign-In did not return an idToken');
      }
      const credential = GoogleAuthProvider.credential(googleResult.idToken);
      return signInWithCredential(this.firebase.fbAuth, credential);
    }
    const provider = new GoogleAuthProvider();
    if (opts?.loginHint) provider.setCustomParameters({ login_hint: opts.loginHint });
    return this.oauthSignInAsync(provider);
  }

  async signInWithFacebookAsync(): Promise<any> {
    console.log('Auth: Starting sign-in using Facebook OAuth');
    return this.oauthSignInAsync(new FacebookAuthProvider());
  }

  async signInWithAppleAsync(): Promise<any> {
    console.log('Auth: Starting sign-in using Apple OAuth');
    if (isCapacitorNative()) {
      // S4 (#3986): same plugin-init ordering gate as the Google path above
      await getMyApp().whenSocialLoginReadyAsync();
      let res;
      try {
        res = await SocialLogin.login({ provider: 'apple', options: {} });
      } catch (err: any) {
        console.error('Auth: SocialLogin.login(apple) failed:', describeErrorForLog(err));
        if (err?.message?.includes('cancel') || err?.code === 'SIGN_IN_CANCELED') {
          const cancelError = new Error('Sign-in cancelled') as any;
          cancelError.code = 'auth/popup-closed-by-user';
          throw cancelError;
        }
        throw err;
      }
      const appleResult = res.result as { idToken: string | null; authorizationCode?: string };
      if (!appleResult?.idToken) {
        throw new Error('Auth: Apple Sign-In did not return an idToken');
      }
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({ idToken: appleResult.idToken });
      const signInResult = await signInWithCredential(this.firebase.fbAuth, credential);
      // Capture Apple authorization_code and exchange server-side for a
      // refresh_token so deleteUser can later call /auth/revoke at
      // account-deletion time (closes the SiwA GDPR account-deletion
      // gap — without this, deleted accounts linger under iPhone
      // Settings > Apple ID > "Apps Using Apple ID"). The plugin
      // populates `authorizationCode` on every login response because
      // app.component.ts initializes Apple with
      // `useProperTokenExchange: true`. Apple invalidates the prior
      // refresh_token on each new authorization, so capturing on EVERY
      // successful sign-in is correct. Best-effort: a failure here
      // must not block sign-in itself.
      if (appleResult.authorizationCode) {
        try {
          await this.cloudFunctionsService.exchangeAppleAuthCodeAsync({ authorizationCode: appleResult.authorizationCode });
        } catch (err: any) {
          this.logging.trackSilentProblem('apple-siwa-exchange-failed', {
            code: err?.code,
            message: err?.message ?? String(err),
          });
        }
      }
      return signInResult;
    }
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return this.oauthSignInAsync(provider);
  }

  private async oauthSignInAsync(provider: AuthProvider): Promise<any> {
    // the web build creates Auth without the popup resolver (#6743), so every popup names it
    return signInWithPopup(this.firebase.fbAuth, provider, browserPopupRedirectResolver);
  }

  /**
   * Web only: whether a tap on a social sign-in button must wait for Google's sign-in frame
   * first. Opening the popup only after that wait lets Safari block it, so the login page waits
   * and asks for a second tap instead (#6743). False once the frame is loaded, and false when
   * the warm-up cannot run at all, where the popup loads the frame itself as it always did.
   */
  signInPopupStillLoading(): boolean {
    if (isCapacitorNative()) return false;
    const state = this.firebase.popupResolverState;
    return state != PopupResolverWarmupState.Ready && state != PopupResolverWarmupState.Unavailable;
  }

  /**
   * Web only: loads Google's sign-in frame in the background, for a screen that shows social
   * sign-in buttons (#6743). Resolves true once it is loaded. Never throws: an outcome that
   * needs a look is reported here, and sign-in then works the way it did before the warm-up.
   */
  async warmSignInPopupAsync(): Promise<boolean> {
    const result = await this.firebase.warmPopupResolverAsync();
    switch (result.outcome) {
      case PopupResolverWarmupOutcome.Ready:
        return true;
      case PopupResolverWarmupOutcome.Unavailable:
        // a Firebase upgrade moved the internal function; the first tap on Safari may now be blocked again
        this.logging.trackSilentProblem('PopupResolverWarmupUnavailable', {});
        return false;
      case PopupResolverWarmupOutcome.Failed: {
        // a failed load because the network dropped is expected on a bad connection, and the
        // next warm-up or tap tries again; anything else is a fault worth seeing
        const code = (result.error as { code?: string } | null)?.code;
        if (code === 'auth/network-request-failed' || isTransientFetchError(result.error)) {
          console.log('Auth: loading the sign-in frame failed on the network, will retry on the next tap:', describeErrorForLog(result.error));
        } else {
          this.logging.trackSilentProblem('PopupResolverWarmupFailed', errorReportFields(result.error));
        }
        return false;
      }
    }
  }

  async signInWithTokenAsync(signInToken: string) {
    this.signingIn = true;
    try {
      console.log('Auth: Starting sign-in using token');
      let result = await signInWithCustomToken(this.firebase.fbAuth, signInToken);
      if (result.user) {
        this.telemetry.trackEvent('login', { method: 'otl' });
      }
    } catch (error) {
      // OTP failed — emit the deferred null auth so app proceeds as not-logged-in
      await this.stopSuppressingNullAuthAsync();

      if (isTokenExpired(signInToken)) {
        console.log('Auth: Firebase auth failed due to expired token');
        throw new Error('EXPIRED_TOKEN');
      }

      console.log('Auth: Firebase auth failed but token is not expired');
      throw error;
    } finally {
      this.signingIn = false;
    }
  }

  /**
   * Stop holding back the "nobody is signed in" signal, and emit it now.
   *
   * A boot that finds a `signInToken` in the URL sets `suppressNullAuth` so the
   * login page does not flash up while the token is being used. Nothing else
   * lifts that hold, so whoever decides the token is not going to work has to
   * lift it — otherwise the app has no screen to show at all.
   *
   * Two callers: the failure path above, and the wait limit in
   * `deep-link-signin.ts` (df-993c16916483), where the sign-in has not failed
   * and is still running. That second case is safe because a sign-in that lands
   * later comes back through `onLoginStateChange` with a user and signs the
   * person in from wherever the app has got to.
   *
   * Does nothing when the hold was never taken.
   */
  async stopSuppressingNullAuthAsync(): Promise<void> {
    if (!this.suppressNullAuth) return;
    this.suppressNullAuth = false;
    await this.onLoginStateChange(null);
  }

  /** Get database with all user info. Only available to admins. */
  public async getAllUsersDbAsync(): Promise<DatabaseWrapper> {
    if (this.allUsersDb) {
      return this.allUsersDb;
    }

    this.allUsersDb = this.dbProvider.createDatabaseWrapper();
    this.allUsersDb.setRemoteOnly();
    await this.allUsersDb.initAsSharedDatabaseAsync(this.user, '_users');

    return this.allUsersDb;
  }

  /** Call this at the start of ionViewCanEnter() in every page, to make sure the app is initialized and user authenticated.  */
  public async pageAuthGuardAsync(minRole: AuthRole = AuthRole.Unknown): Promise<void> {
    let error = undefined;

    // if any special role is required
    if (minRole > AuthRole.Unknown) {
      // if user is not authenticated, bail
      if (!this.user) {
        error = 'Auth: Must be logged in to access this page.';
      }
      let role = this.getAuthRole();
      if (minRole > role) {
        error = `Auth: Not enough permissions to access this page. (${role}<${minRole})`;
      }
    }

    if (error) {
      this.logging.trackAbnormalError(error);
      // PageGuardRefusalError, not a plain Error: this refusal is deliberate and the line
      // above has just reported it, so cs-nav must not report it a second time when it
      // catches this. See PageGuardRefusalError in providers/page-lifecycle-guard.
      throw new PageGuardRefusalError(error);
    }
  }

  /**
   * Call this from a page's ionViewCanEnter() when the page cannot be shown for a reason other
   * than authentication — typically the record it was opened for does not exist, or could not
   * be read. Reports the failure, tells the user, and then throws so the page stops its own
   * setup and cs-nav cancels the navigation.
   *
   * It deliberately does NOT navigate anywhere. It runs inside the very call chain that is
   * about to cancel this navigation (cs-nav's setPageActive), and starting a second navigation
   * from in there races that cancel over the page stack (see design-docs/navigation.md, "A
   * refused navigation and a navigation its guard starts"). The recovery for a page
   * that fails at boot — where cancelling leaves the app with no page at all — lives at the end
   * of NavController.initializeNavigationSystem instead, which runs after everything settled.
   * See #4681.
   *
   * Declared as returning void even though it always throws. Every caller writes
   * `return this.auth.failPageGuardAsync(...)` inside a hook that also has plain `return;`
   * statements, and typing this as `Promise<never>` makes TypeScript reject all of those for
   * mixing a value-returning path with a bare one.
   *
   * `message` is for us — it goes to the console and to Sentry, never to the screen.
   * `userMessage` is the optional toast text for the reasons where the default ("it may have
   * been deleted") would be actively wrong, such as a page that cannot open because a
   * computation behind it failed. Leave it out and the default is used.
   * `cause` is the error that made the page give up, when there was one. Pass it: without it a
   * database that could not be read is reported exactly like a record that does not exist, and
   * nobody can tell the two apart (#5191).
   */
  public async failPageGuardAsync(
    message: string,
    { userMessage, cause }: { userMessage?: TranslatedString; cause?: unknown } = {},
  ): Promise<void> {
    // PageGuardRefusalError, not a plain Error: this refusal is deliberate and the lines
    // below report it, so cs-nav must not report it a second time when it catches this.
    // See PageGuardRefusalError in providers/page-lifecycle-guard.
    let err = new PageGuardRefusalError(message);
    console.error(err);
    // Report under one stable name so every page's load failures group as a single Sentry
    // issue, with the specific reason carried in the context rather than in the title.
    // Before #4681 this reported nothing at all, so a user could sit on a page that had
    // silently failed to load and we never saw it.
    this.logging.trackSilentProblem('PageLoadFailed', {
      message,
      url: typeof location !== 'undefined' ? location.pathname : undefined,
      cause: cause === undefined ? undefined : describeError(cause),
    });
    this.toastCtrl.showToast({
      message: userMessage ?? LL.errors.userFacing.couldNotOpenPage(),
      duration: 4000,
    });
    throw err;
  }

}
